/**
 * ONE TAP — the VFX system.
 *
 * One object that owns every visual effect in the game and maps the simulation's
 * `GameEvent` stream onto it. The sim never knows this file exists; this file never
 * writes to the sim. They meet only at typed state (DESIGN.md 6).
 *
 * What lives here:
 *
 *   - the event map itself: every single `GameEvent` variant gets a visual, no exceptions
 *     (DESIGN.md 7 — nothing happens silently, and nothing happens invisibly either);
 *   - PULSE's kinetic shockwave, which is a real screen-space refraction: the shell grabs
 *     the frame behind it and re-samples it with a chromatic radial offset, so it visibly
 *     bends the world instead of merely drawing a ring on top of it;
 *   - the screen-space graphic treatments: the wounded player's persistent edge rim,
 *     HIGH NOON, the round wipes and the one-shot comic bursts (headshot, disarm, catch);
 *   - the sudden-death gun beacons, drawn through geometry exactly as DESIGN.md 2 asks.
 *
 * THE READABILITY CONTRACT, which outranks every other consideration in this file:
 *
 *   1. No screen-space effect ever writes inside the central ~20% of the frame. The
 *      one-shot bursts and HIGH NOON multiply by a `centreHole` term; the wound rim,
 *      which is the only PERSISTENT overlay in the game, is held to an explicit area
 *      budget measured from the frame EDGE instead (see WOUND_RIM_REACH), because a
 *      radial guard on a 16:9 frame still lets an effect cover the top and bottom thirds
 *      of the image. A beautiful effect that hides the crosshair or an enemy is a bug.
 *   2. Big effects are built from expanding rings and radiating spikes, never solid
 *      masses, so you can always see through them to the fight. Where a solid shape is
 *      genuinely the right graphic — the four-point star — it is clamped against its
 *      distance from the player's eye (see SOLID_MAX_ANGULAR), so an authored size that
 *      is honest across the atrium cannot become a screen wipe at point-blank range.
 *   3. Everything is brief. The longest one-shot in this file is 700 ms, and the only
 *      persistent overlay is a rim at 15% opacity.
 *   4. Anything anchored to the LOCAL player's hands is emitted a metre from the lens,
 *      where world-scale numbers become screen-filling ones. Those effects get their
 *      own reduced scale and are pushed down the barrel (`LOCAL_*` below).
 *
 * AND THE BRIGHT-WORLD RULE (see particles.ts for the long version): the readable body
 * of every effect here is MATTE — an opaque, saturated, ink-outlined graphic shape.
 * Additive is only ever the incandescent core sitting inside one. The same goes for the
 * screen overlays: they are drawn with source-over, as printed marks on the frame, not
 * added to it, because a sunlit frame has no headroom left to add light to.
 *
 * COLOUR (DESIGN.md 4, and the `VFX` contract in particles.ts): team colour, amber for
 * the chamber, red for danger, ink for everything that is merely physics. There is no
 * fifth colour, and movement — dashing, wall-running, grappling — gets no colour of its
 * own at all, because none of it is information anyone has to act on.
 *
 * Camera shake and the two-frame post-process ink splat live in the Renderer, which this
 * class is not given a handle to. Wire them through {@link VfxSystem.hooks}; every effect
 * here still reads correctly if you never do.
 *
 * Client-only: touches `three`. Never import from src/shared.
 */

import * as THREE from 'three';
import {
  FOV_BASE,
  GRAPPLE_AIM_CONE,
  GRAPPLE_RANGE,
  PALETTE,
  PLAYER_CROUCH_EYE_HEIGHT,
  PLAYER_EYE_HEIGHT,
  PLAYER_HEIGHT,
  PULSE_RANGE,
  TEAM_COLORS,
} from '@shared/constants';
import { clamp01, dirFromYawPitch, rightFromYaw, v3, type Vec3 } from '@shared/math';
import { ARENA } from '@shared/sim/arena';
import {
  GunState,
  HealthState,
  HitZone,
  SurfaceKind,
  type GameEvent,
  type GrappleAnchor,
  type GunEntity,
  type PlayerState,
  type WorldState,
} from '@shared/types';
import {
  applyVfxBlend,
  P_CHIP,
  P_DOT,
  P_INK,
  P_PUFF,
  P_RING,
  P_SPARK,
  P_STAR,
  ParticleSystem,
  VFX,
} from './particles';
import { TrailSystem } from './trails';
import { ImpactSystem } from './impact';

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const SHOCKWAVE_POOL = 4;
const SHOCKWAVE_LIFE = 0.42;
/** Screen-space refraction offset at the shell's silhouette, in UV. */
const SHOCKWAVE_REFRACT = 0.055;
/** How far past PULSE_RANGE the shell coasts. */
const SHOCKWAVE_REACH = 1.25;

/**
 * YOUR OWN PULSE, SEEN FROM INSIDE YOUR OWN HEAD.
 *
 * The sim reports the pulse origin as the caster's EYE, which is exactly where the
 * camera is — and an effect anchored exactly at the camera does not merely look wrong,
 * it does not draw at all:
 *
 *   - the refraction shell degenerates. Its fragment normals and the view vector are
 *     antiparallel everywhere on a sphere centred on the eye, so `rim = 1 - |dot(n,v)|`
 *     is zero across the whole shell and every fragment discards;
 *   - the shock ring is a billboard whose centre sits at view-space z = 0, so the entire
 *     quad falls inside the near plane and is clipped.
 *
 * The highest-value play available to an unarmed player was therefore, for the person
 * actually making it, a handful of dots and a camera tap.
 *
 * So the whole local pulse is pushed out in front of the caster, down the cone the shove
 * actually travels along, and the shell is given a shorter reach chosen against that
 * push so the camera stays OUTSIDE it for the shell's entire life. It then reads the way
 * it reads for everybody else: a hard lens ring punching forward.
 */
const LOCAL_PULSE_PUSH = 2.4;
/** Shell radius peaks at 0.4 + this, so 1.7 keeps 0.3 m of clearance from the lens. */
const SHOCKWAVE_LOCAL_REACH = 1.7;

const BEACON_POOL = 4;
const BEACON_HEIGHT = 36;
const BEACON_WIDTH = 1.5;
/** Intersecting planes per beacon. Three gives it a solid read from any angle. */
const BEACON_PLANES = 3;
/**
 * Hard ceiling on how solid ONE PLANE of a beacon is allowed to get.
 *
 * A beacon is drawn through walls, so its opacity is a readability guarantee rather than
 * a taste call: past a certain point the beam is cover an enemy can stand behind. The
 * number is per-plane and the planes all pass through the beam's axis, so near the axis
 * the alpha compounds `1 - (1 - a)^BEACON_PLANES`. At the old 0.55 that came out at 0.91
 * — a near-solid 36 m column, which is precisely the thing the contract forbids. 0.24
 * compounds to about 0.56, so the stacked beam lands where a single plane was meant to.
 */
const BEACON_PEAK_ALPHA = 0.24;
/**
 * Fraction of the beam's height over which it fades UP from nothing at its foot.
 *
 * The beam is drawn through geometry and its density falls off with height, so it used to
 * be at its most solid at exactly ground level — which is where the gun is, and therefore
 * where whoever is fighting over it is standing. A translucent column veiling the player
 * you are shooting at is the readability contract's first clause, broken by the effect
 * whose entire job is to tell you where people are. 0.06 of 36 m is a little over two
 * metres, so the beam is clear from the floor to above head height and the column above
 * carries the signal.
 */
const BEACON_FOOT = 0.06;

const BURST_OVERLAYS = 3;

/** How often a wounded player re-emits their world-space marker. */
const WOUND_MARKER_PERIOD = 0.55;
/** Frames between sweeps of the wound-marker map for players who left the snapshot. */
const WOUND_MARKER_SWEEP_FRAMES = 60;

/**
 * Diameter of the ink the arena keeps where a player died. A head kill leaves a wider
 * mark than a body kill, so the floor of a round is a readable record of how it went.
 */
const KILL_SPLAT_BODY = 2.4;
const KILL_SPLAT_HEAD = 3.2;

/** Overlay quads sit this far in front of the camera, well inside the near plane. */
const OVERLAY_DIST = 0.5;

/**
 * THE WOUND RIM.
 *
 * Being wounded is a persistent state, and the previous treatment expressed it as a
 * full-frame radial wash: it reached from all four corners deep into the middle of the
 * image, desaturated the arena and arrived on the exact frame the player most needs to
 * read the room. A state that lasts until you are healed cannot be allowed to cost you
 * the picture — so it is now a genuine edge rim and nothing else.
 *
 * The numbers are an area budget, not a taste call. `WOUND_RIM_REACH` is the band's
 * thickness measured in units of frame HEIGHT (so it is the same physical thickness on
 * the sides as on the top and bottom at any aspect), and the wobble is the only thing
 * allowed to push it further in:
 *
 *   worst-case reach = REACH * (1 + WOBBLE) = 0.0496
 *   at 16:9 that covers 1 - (1.7778 - 2r)(1 - 2r) / 1.7778 = 15.0% of the frame's area
 *   the innermost tinted pixel sits 0.0496 of the frame height from the edge, where the
 *   central 60% of the frame begins at 0.1427 — so the middle of the image is untouched
 *   by a factor of nearly three, with the wobble at full extension.
 *
 * The pulse is therefore an OPACITY event, never a geometry one: a damage hit takes the
 * same rim from its persistent 15% up to 35% and lets it fall back over a quarter of a
 * second. The band never grows.
 *
 * THIS IS THE ONLY WOUND OVERLAY IN THE PRODUCT. The HUD used to draw a second one in the
 * DOM — a radial red wash over roughly 60% of the frame, flashing twice a second — and
 * the two of them were silently summing. The DOM layer is gone (see `.ot-hud__rim` in
 * ui/style.css, which is now the eliminated state only). If you add a wounded treatment
 * anywhere else, these numbers stop meaning what they say.
 */
const WOUND_RIM_REACH = 0.042;
const WOUND_RIM_WOBBLE = 0.18;
const WOUND_ALPHA_STEADY = 0.15;
const WOUND_ALPHA_PEAK = 0.35;
const WOUND_PULSE_SEC = 0.25;

/**
 * THE ANGULAR CAP ON SOLID SHAPES.
 *
 * A four-point star is the one filled silhouette in the vocabulary, and a filled
 * silhouette is authored at a world size that assumes you are watching it from across the
 * room. Authored sizes are honest at ten metres and a screen wipe at two: a 3.6 m disarm
 * star seen from 1.5 m is 100 degrees across, drawn opaque, on the frame where the player
 * has to find the person who just took the gun off them.
 *
 * So every solid shape is clamped to subtend at most this fraction of its distance, which
 * at the base 92 degree FOV works out at roughly a third of the frame height:
 *
 *   size <= dist * SOLID_MAX_ANGULAR  =>  2 * atan( 0.31 ) = 34 degrees
 *
 * Far away nothing changes at all — the cap only ever bites when the effect is close
 * enough to be a problem, which is exactly the case the authored numbers get wrong.
 * Hollow shapes (rings) and debris showers are deliberately exempt: you can see straight
 * through both, and shrinking them would cost readability rather than buy it.
 */
const SOLID_MAX_ANGULAR = 0.62;
/** Below this the effect is effectively at the lens; clamping further just deletes it. */
const SOLID_MIN_DIST = 0.6;

/**
 * Anything the LOCAL player emits from their own hands is roughly a metre from the lens,
 * so a 2 m ring sized for the world becomes a 2 m ring across the crosshair. Hand
 * effects are shrunk by this and pushed this far further down the barrel.
 */
const LOCAL_HAND_SCALE = 0.5;
const LOCAL_HAND_PUSH = 0.6;

/**
 * THE BODY-CENTRED EFFECT RULE.
 *
 * Rings and showers anchored to a player's torso are fine seen from the outside and fine
 * seen from the inside: a ring is hollow and sweeps off the edge of the frame, and debris
 * flies away from you within a few frames. A FILLED shape is not. A four-point star is
 * the one solid silhouette in the vocabulary, and a solid silhouette centred on the
 * camera is a screen-filling wipe no matter what size it is authored at — there is no
 * number small enough, because the distance to it is zero.
 *
 * So the stars belong to the third-person read only. When the effect is anchored on the
 * LOCAL player's own body they are dropped, and the screen-space burst overlay carries
 * the beat instead — which is what it is for, and which respects `centreHole`.
 *
 * This matters most at exactly the moments it used to fail: being disarmed and being
 * healed. Both used to end with a two-and-a-half-metre incandescent star drawn at the
 * lens, i.e. a white-out, in the half second you most need to see who did it to you.
 */


/**
 * THE SHAKE BUDGET.
 *
 * Trauma is clamped to 1 and decays exponentially, so it STACKS: a headshot kill fires
 * the shot hook, the kill hook and the headshot bonus inside the same frame. Tuned
 * naively that saturates the camera on the single moment the player most needs to see
 * clearly — where the rest of the enemy team is standing.
 *
 * The rule is that shake PUNCTUATES. It marks that something happened to *you*; it is
 * not a volume knob on how exciting the event was. So the numbers below are budgeted as
 * a whole, worst case first, and anything that happens to somebody else across the arena
 * gets a tap or gets nothing.
 *
 *   dying            the loudest thing that can happen. Nothing else comes close.
 *   being wounded    loud: your speed and your survivability just changed.
 *   being disarmed   loud: you lost the round's most important object.
 *   your headshot    a crisp snap, deliberately UNDER being hit — you want to see.
 *   everything else  taps.
 */
const SHAKE_DEATH = 0.62;
const SHAKE_WOUNDED = 0.36;
const SHAKE_DISARMED = 0.34;
const SHAKE_DISARM_BY_ME = 0.22;
const SHAKE_MY_HEADSHOT = 0.24;
const SHAKE_MY_BODYSHOT = 0.14;
const SHAKE_MY_MISS = 0.10;
/** Somebody else's kill, seen from inside the fight. A nudge, not an event. */
const SHAKE_KILL_NEARBY = 0.12;
const SHAKE_PULSE_HIT = 0.24;
const SHAKE_PULSE_WHIFF = 0.12;
const SHAKE_CATCH_CHAMBERED = 0.12;
const SHAKE_CATCH_EMPTY = 0.06;
const SHAKE_DRYFIRE = 0.03;
const SHAKE_DOUBLE_JUMP = 0.04;
const SHAKE_DASH = 0.05;
const SHAKE_GRAPPLE_BOOST = 0.10;
const SHAKE_ROUND_END = 0.16;
const SHAKE_SUDDEN_DEATH = 0.24;
const SHAKE_MATCH_END = 0.4;

/**
 * THE GRAPPLE RETICLE — the fix for a verb nobody could find.
 *
 * Playtest: *"I can't really figure out how to get the grapple to work. I tried using it,
 * but it just didn't do anything."* The anchors were in range the whole time. Nothing on
 * screen ever said so, and a traversal verb with no affordance is not a verb — it is a
 * keybinding in a manual nobody read (DESIGN.md 8: the game must be comprehensible
 * without instruction).
 *
 * So every anchor the local player could plausibly hook wears a mark, and the ONE that
 * would actually be hooked if the key were pressed right now wears a different, louder
 * one. The two states are the same four brackets at two different radii, so the
 * transition between them is a single continuous convergence — the universal "target
 * acquired" gesture, which needs no teaching at all.
 *
 * Rules it obeys:
 *
 *   - **Constant angular size.** The mark subtends a fixed fraction of frame HEIGHT
 *     whatever the distance, so it is a screen-space reticle pinned to a world point,
 *     not a decal that vanishes at forty metres. Sizes below are that fraction.
 *   - **No hue.** Movement gets no colour of its own in this game (see the COLOUR note in
 *     this file's header) — the mark is the crosshair's own two-tone contract, a white
 *     core inside a near-black edge, which is also why it survives a sunlit wall and a
 *     shadowed ceiling without sampling either.
 *   - **Depth-tested.** An anchor behind a wall is not drawn, which is both honest and a
 *     free stand-in for the sim's line-of-sight test.
 *   - **Quiet until it matters.** A dormant mark is small, thin and translucent; it says
 *     "there is a hook here" and nothing more. Only the acquired one is allowed to be
 *     loud, and only one is ever acquired.
 */
const ANCHOR_MARK_POOL = 24;
/**
 * Fraction of frame height the mark subtends: dormant, and once acquired.
 *
 * These are close together on purpose. The acquisition gesture is the BRACKETS CLOSING
 * (see the mix on `off` in the fragment shader, 0.46 -> 0.24 of the quad), not the quad
 * growing — if the quad grew as much as the brackets converged, the corners would end up
 * in almost the same place on screen and the whole read would cancel out. The small pop
 * in overall size is punctuation on top of that, nothing more.
 */
const ANCHOR_SIZE_DORMANT = 0.050;
const ANCHOR_SIZE_LOCKED = 0.058;
/** Dormant marks fade out between this multiple of GRAPPLE_AIM_CONE and the next. */
const ANCHOR_FADE_CONE_IN = 1.0;
const ANCHOR_FADE_CONE_OUT = 1.9;
/** Distance fade: full until this fraction of GRAPPLE_RANGE, gone at the range itself. */
const ANCHOR_FADE_DIST = 0.86;
/** Peak opacity of a dormant mark. The acquired one always reaches 1. */
const ANCHOR_DORMANT_ALPHA = 0.5;
/** Marks dim to this while the grapple is on cooldown — the cooldown, taught for free. */
const ANCHOR_COOLING_DIM = 0.34;
/** Lock converges fast and lets go slower, so acquisition snaps and loss does not flicker. */
const ANCHOR_LOCK_RATE_ON = 30;
const ANCHOR_LOCK_RATE_OFF = 15;
/** Below this a mark is not worth an instance. */
const ANCHOR_MIN_ALPHA = 0.02;
/**
 * How far toward the eye the mark is slid, as a fraction of its distance, so the anchor's
 * own body cannot depth-reject the reticle drawn on it. 5% is 1.5 m at thirty metres and
 * 15 cm at three, which clears the prop at every range without ever being enough to punch
 * through a wall that is genuinely between the player and the anchor.
 */
const ANCHOR_DEPTH_BIAS = 0.95;

// One-shot screen burst kinds. Kept as plain numbers so they can be a shader uniform.
const BURST_ROUND_START = 1;
const BURST_ROUND_END = 2;
const BURST_HEADSHOT = 3;
const BURST_DISARM = 4;
const BURST_CATCH = 5;
const BURST_KILL = 6;
const BURST_LAUNCH = 7;

const BURST_DURATION: Record<number, number> = {
  [BURST_ROUND_START]: 0.70,
  [BURST_ROUND_END]: 0.65,
  [BURST_HEADSHOT]: 0.42,
  [BURST_DISARM]: 0.50,
  [BURST_CATCH]: 0.34,
  [BURST_KILL]: 0.30,
  [BURST_LAUNCH]: 0.36,
};

/**
 * Peak coverage of each burst, now that they are drawn source-over instead of added.
 * The headshot gets the most because it is the game's peak moment; the catch gets the
 * least because it happens several times a round and must never become wallpaper.
 */
const BURST_OPACITY: Record<number, number> = {
  [BURST_ROUND_START]: 0.55,
  [BURST_ROUND_END]: 0.55,
  [BURST_HEADSHOT]: 0.85,
  [BURST_DISARM]: 0.6,
  [BURST_CATCH]: 0.45,
  [BURST_KILL]: 0.5,
  // The release boost. Loud enough that a well-timed release is unmistakably a REWARD —
  // the skill of the whole verb lives in that instant — and short enough that it is over
  // before the speed it granted matters.
  [BURST_LAUNCH]: 0.6,
};

// ---------------------------------------------------------------------------
// Shared GLSL
// ---------------------------------------------------------------------------

const OVERLAY_VERT = /* glsl */ `
varying vec2 vUv;

void main() {

	vUv = uv;
	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

}
`;

const OVERLAY_COMMON = /* glsl */ `
float hash11( float p ) {

	p = fract( p * 0.1031 );
	p *= p + 33.33;
	p *= p + p;
	return fract( p );

}

/**
 * THE readability guard. Returns 0 in the middle of the frame and ramps to 1 outward, so
 * no screen treatment in this game is capable of covering the crosshair or the enemy the
 * player is aiming at.
 */
float centreHole( float r, float inner, float outer ) {

	return smoothstep( inner, outer, r );

}
`;

// ---------------------------------------------------------------------------
// Wound overlay — a tight rim on the frame edge, and nothing else
// ---------------------------------------------------------------------------

/**
 * Deliberately NOT built on `centreHole`. That guard is a radial one, and a radial guard
 * on a 16:9 frame lets an effect reach a very long way in along the vertical axis before
 * it trips: the corners are 1.4x further from the middle than the top edge is, so a
 * radius tuned to leave the corners alone has already covered the top and bottom thirds
 * of the image. This overlay measures distance to the nearest EDGE instead, which is the
 * only measure under which "outer 15% of the frame" means what it says.
 */
const WOUND_FRAG = /* glsl */ `
uniform float uSteady;
uniform float uPulse;
uniform float uTime;
uniform float uAspect;
uniform vec3 uRim;
uniform vec3 uHot;

varying vec2 vUv;

void main() {

	if ( uSteady <= 0.002 && uPulse <= 0.002 ) discard;

	// Distance to the nearest frame edge, in units of frame HEIGHT.
	vec2 c = abs( vUv - 0.5 );
	float edge = min( ( 0.5 - c.x ) * uAspect, 0.5 - c.y );

	// Enough irregularity that the rim is a drawn mark rather than a photographic
	// vignette, and no more: this term is inside the area budget, not on top of it.
	vec2 d = vUv - 0.5;
	float ang = atan( d.y, d.x );
	float wob = 1.0 + ${(WOUND_RIM_WOBBLE * 0.62).toFixed(4)} * sin( ang * 5.0 + 1.3 )
		+ ${(WOUND_RIM_WOBBLE * 0.38).toFixed(4)} * sin( ang * 9.0 - 2.1 );

	float reach = ${WOUND_RIM_REACH.toFixed(4)} * wob;
	float band = 1.0 - smoothstep( reach * 0.22, reach, edge );
	if ( band <= 0.004 ) discard;

	// Two thumps per cycle: a heartbeat, not a sine wave. It breathes the rim's opacity
	// and never its width.
	float p = fract( uTime * 1.15 );
	float beat = clamp( exp( - 24.0 * p ) + 0.66 * exp( - 26.0 * abs( p - 0.20 ) ), 0.0, 1.0 );

	float amt = uSteady * ${WOUND_ALPHA_STEADY.toFixed(3)} * ( 0.80 + 0.20 * beat )
		+ uPulse * ${(WOUND_ALPHA_PEAK - WOUND_ALPHA_STEADY).toFixed(3)};

	float a = clamp( band * amt, 0.0, ${WOUND_ALPHA_PEAK.toFixed(3)} );
	if ( a <= 0.003 ) discard;

	// Dried blood at rest, hot red on the hit. Source-over and premultiplied: this is a
	// mark printed on the border of the frame, not a filter over the image.
	vec3 col = mix( uRim, uHot, clamp( uPulse * 0.8 + beat * 0.18, 0.0, 1.0 ) );
	gl_FragColor = vec4( col * a, a );

}
`;

// ---------------------------------------------------------------------------
// HIGH NOON overlay — persistent, sudden death
// ---------------------------------------------------------------------------

const NOON_FRAG = /* glsl */ `
uniform float uAmount;
uniform float uTime;
uniform float uAspect;
uniform vec3 uColor;

varying vec2 vUv;

${OVERLAY_COMMON}

void main() {

	if ( uAmount <= 0.002 ) discard;

	vec2 c = ( vUv - 0.5 ) * vec2( uAspect, 1.0 );
	float r = length( c ) * 1.42;
	float ang = atan( c.y, c.x );

	// A slow, ACHROMATIC breathing rim. Nothing more.
	//
	// This used to print amber over the border with rising heat bars and spokes, tinted with
	// PALETTE.loaded — the exact colour of a chambered gun. At sudden death that spent the whole
	// colour budget on a state change AND put the gun's "who is armed" glow in direct competition
	// with the frame edge, in the one minute of the round where that read matters most. It also
	// recoloured the white city to sepia, which DESIGN.md §4 forbids outright.
	//
	// The renderer now carries the escalation properly and without moving any hue: the sun drops
	// to 28deg so every shadow lengthens, contrast gamma tightens, and sun glare rises. See
	// Renderer.setHighNoon. All this overlay does is confirm the state at the very edge of frame.
	float pulse = 0.55 + 0.45 * sin( uTime * 2.1 );
	float rim = smoothstep( 0.78, 1.20, r );

	float m = rim * ( 0.10 + 0.12 * pulse ) * uAmount;
	m *= centreHole( r, 0.62, 0.99 );
	if ( m <= 0.003 ) discard;

	// Source-over, premultiplied: HIGH NOON is amber PRINTED on the border of the frame.
	// Added, it would have been a faint warm glaze on an already-sunlit image — the
	// loudest state change in the match rendered as almost nothing.
	float a = clamp( m * 0.8, 0.0, 1.0 );
	gl_FragColor = vec4( uColor * a, a );

}
`;

// ---------------------------------------------------------------------------
// One-shot graphic bursts
// ---------------------------------------------------------------------------

const BURST_FRAG = /* glsl */ `
uniform float uKind;
uniform float uProgress;
uniform float uAspect;
uniform float uSeed;
uniform float uOpacity;
uniform vec3 uColor;

varying vec2 vUv;

${OVERLAY_COMMON}

void main() {

	if ( uKind < 0.5 || uProgress >= 1.0 ) discard;

	vec2 c = ( vUv - 0.5 ) * vec2( uAspect, 1.0 );
	float r = length( c ) * 1.42;
	float ang = atan( c.y, c.x );
	float t = clamp( uProgress, 0.0, 1.0 );
	float fade = 1.0 - t;

	float m = 0.0;
	vec3 col = uColor;

	if ( uKind < 1.5 ) {

		// ROUND START — two hard bars wipe across from opposite corners and leave, plus
		// a rim flash. Reads as a printed frame change.
		float d = ( c.x * 0.7071 + c.y * 0.7071 ) * 0.7 + 0.5;
		float head = t * 2.2 - 0.4;
		float bar = ( 1.0 - smoothstep( 0.0, 0.12, abs( d - head ) ) );
		float bar2 = ( 1.0 - smoothstep( 0.0, 0.08, abs( ( 1.0 - d ) - head ) ) );
		m = ( bar * 0.75 + bar2 * 0.45 ) * fade;
		m += smoothstep( 0.78, 1.25, r ) * fade * fade * 0.55;

	} else if ( uKind < 2.5 ) {

		// ROUND END — a rim slam with halftone dots thrown outward from the border.
		float slam = exp( - 5.0 * t );
		m = smoothstep( 0.70, 1.20, r ) * slam * 0.8;
		vec2 g = ( vUv * vec2( uAspect, 1.0 ) ) * 26.0;
		vec2 cell = fract( g ) - 0.5;
		float dots = 1.0 - smoothstep( 0.16, 0.34, length( cell ) );
		m += dots * smoothstep( 0.86, 1.24, r ) * slam * 0.5;

	} else if ( uKind < 3.5 ) {

		// HEADSHOT — a hard shock ring sprinting outward with eight radiating spikes.
		// The peak moment of the game, and it still leaves the middle of the frame clear.
		// It is a DRAWN LINE, deliberately thin: this beat is about timing, not about
		// covering the screen in gold.
		float ringR = 0.30 + t * 1.20;
		float w = 0.018 + t * 0.045;
		float ring = 1.0 - smoothstep( w * 0.4, w, abs( r - ringR ) );
		float spike = pow( abs( sin( ang * 4.0 + uSeed * 6.28 ) ), 26.0 );
		float spikes = spike * smoothstep( 0.30, 0.70, r ) * ( 1.0 - smoothstep( ringR, ringR + 0.30, r ) );
		float flash = exp( - 18.0 * t ) * smoothstep( 0.80, 1.25, r );
		m = ( ring * 0.95 + spikes * 0.55 + flash * 0.7 ) * fade;

	} else if ( uKind < 4.5 ) {

		// DISARM — comic impact lines converging from the border. The highlight play, and
		// it gets the loudest graphic in the game short of a headshot.
		float a = ang * 0.15915494 + 0.5;
		float n = a * 44.0;
		float id = floor( n );
		float f = fract( n );
		float wsel = 0.16 + 0.5 * hash11( id * 3.1 + uSeed * 7.0 );
		float streak = 1.0 - smoothstep( wsel * 0.4, wsel, abs( f - 0.5 ) * 2.0 );
		// The lines converge and then STOP. Run in to 0.48 and forty-four opaque spokes
		// arrive over the middle third of the frame on the one beat where the player has
		// to find whoever just took the gun off them.
		float start = mix( 1.30, 0.66, smoothstep( 0.0, 0.55, t ) );
		float radial = smoothstep( start, start + 0.30, r );
		m = streak * radial * ( 1.0 - smoothstep( 0.6, 1.0, t ) ) * 0.95;
		m += smoothstep( 0.80, 1.25, r ) * exp( - 7.0 * t ) * 0.7;

	} else if ( uKind < 5.5 ) {

		// CATCH — a quick team-coloured rim ring. The reward beat: felt, never intrusive.
		float ringR = 1.34 - t * 0.30;
		float ring = 1.0 - smoothstep( 0.02, 0.14, abs( r - ringR ) );
		m = ring * ( 1.0 - smoothstep( 0.5, 1.0, t ) ) * 0.8;

	} else if ( uKind < 6.5 ) {

		// KILL — a fast rim flash under the post-process ink splat.
		m = smoothstep( 0.78, 1.25, r ) * exp( - 9.0 * t ) * 0.85;

	} else {

		// LAUNCH — the grapple release boost. A ring of streaks SPRINTS outward past the
		// border, which is the one graphic that reads unambiguously as "you just gained a
		// lot of speed". It is the mirror of the DISARM burst, whose lines converge
		// inward; nothing else in the vocabulary runs this way, so the boost cannot be
		// confused with being hit by something.
		float a = ang * 0.15915494 + 0.5;
		float n = a * 30.0;
		float id = floor( n );
		float f = fract( n );
		float wsel = 0.20 + 0.45 * hash11( id * 2.7 + uSeed * 5.0 );
		float streak = 1.0 - smoothstep( wsel * 0.4, wsel, abs( f - 0.5 ) * 2.0 );
		// The band the streaks live in travels from mid-frame to past the corner, so they
		// leave the picture entirely rather than piling up at the edge.
		float head = 0.52 + t * 1.25;
		float band = ( 1.0 - smoothstep( head, head + 0.26, r ) ) * smoothstep( head - 0.40, head, r );
		m = streak * band * ( 1.0 - t * t ) * 0.95;
		m += smoothstep( 0.84, 1.28, r ) * exp( - 6.0 * t ) * 0.5;

	}

	m *= centreHole( r, 0.30, 0.56 );
	if ( m <= 0.003 ) discard;

	// Premultiplied source-over. Every burst in this file is a DRAWN mark — a wipe bar,
	// a shock ring, converging impact lines — and a drawn mark has to be able to be
	// darker than what is behind it. Additive could only ever brighten a frame that is
	// already white, which is why none of these registered before.
	float a = clamp( m * uOpacity, 0.0, 1.0 );
	gl_FragColor = vec4( col * a, a );

}
`;

// ---------------------------------------------------------------------------
// Shockwave shell — real screen-space refraction
// ---------------------------------------------------------------------------

const SHOCK_VERT = /* glsl */ `
varying vec3 vN;
varying vec3 vVP;

void main() {

	vec4 mv = modelViewMatrix * vec4( position, 1.0 );
	vN = normalize( normalMatrix * normal );
	vVP = mv.xyz;
	gl_Position = projectionMatrix * mv;

}
`;

const SHOCK_FRAG = /* glsl */ `
uniform sampler2D uScene;
uniform vec2 uResolution;
uniform float uHasScene;
uniform float uProgress;
uniform float uStrength;
uniform vec3 uColor;

varying vec3 vN;
varying vec3 vVP;

void main() {

	vec3 n = normalize( vN );
	vec3 v = normalize( - vVP );
	float rim = 1.0 - abs( dot( n, v ) );

	float fade = 1.0 - uProgress;
	float band = smoothstep( 0.20, 0.95, rim );
	float ring = smoothstep( 0.70, 0.98, rim );

	vec3 col = vec3( 0.0 );
	float a = ring;

	if ( uHasScene > 0.5 ) {

		// Push the sample along the shell's screen-space normal. Splitting the three
		// channels by slightly different amounts turns a plain warp into a lens, which
		// is what makes the shove read as kinetic rather than decorative.
		vec2 uv = gl_FragCoord.xy / uResolution;
		vec2 off = n.xy * uStrength * band * fade;
		col.r = texture2D( uScene, uv + off * 1.18 ).r;
		col.g = texture2D( uScene, uv + off ).g;
		col.b = texture2D( uScene, uv + off * 0.82 ).b;
		a = clamp( band * 1.5 * fade + ring, 0.0, 1.0 );

	}

	col += uColor * ring * ( 1.5 + 5.0 * fade );

	if ( a <= 0.004 ) discard;
	gl_FragColor = vec4( col * a, a );

}
`;

// ---------------------------------------------------------------------------
// Sudden-death beacon
// ---------------------------------------------------------------------------

const BEACON_VERT = /* glsl */ `
varying vec2 vUv;

void main() {

	vUv = uv;
	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

}
`;

const BEACON_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uTime;
uniform float uAmount;
uniform float uPeak;

varying vec2 vUv;

void main() {

	float u = abs( vUv.x - 0.5 ) * 2.0;
	float core = 1.0 - smoothstep( 0.18, 1.0, u );
	float fall = pow( 1.0 - vUv.y, 1.7 );
	// Clear at the foot: nobody fighting over the gun is ever seen through this. See
	// BEACON_FOOT — the beam is a marker above the scramble, never a veil over it.
	fall *= smoothstep( 0.0, ${BEACON_FOOT.toFixed(3)}, vUv.y );

	// Chevrons climbing the beam: it reads as a signal, not as a wall.
	float ch = fract( vUv.y * 5.0 - uTime * 1.6 );
	float band = smoothstep( 0.50, 0.95, ch );

	float a = core * fall * ( 0.45 + 0.55 * band ) * uAmount * uPeak;
	if ( a <= 0.003 ) discard;

	// Source-over, premultiplied. A beacon is a COLUMN OF PAINT standing in the arena,
	// not a light: added to a white atrium under a blue sky it was a faint warm smudge,
	// which is a poor way to announce that nobody's position is a secret any more.
	//
	// It stays translucent on purpose. This beam is drawn THROUGH geometry, so an opaque
	// one would be a 36 m wall an enemy could stand behind — the readability contract
	// forbids that outright, and a tint carries the information just as well.
	gl_FragColor = vec4( uColor * a, a );

}
`;

// ---------------------------------------------------------------------------
// Grapple anchor reticle
// ---------------------------------------------------------------------------

/**
 * One instanced view-facing quad per anchor. The quad is sized in the vertex shader
 * against its own view-space depth, which is what makes it a screen-space reticle pinned
 * to a world point rather than a billboard that shrinks into nothing.
 */
const RETICLE_VERT = /* glsl */ `
attribute vec3 aCenter;
attribute vec2 aState;   // x = visibility 0..1, y = lock 0..1

uniform float uDormant;  // half-angle subtended, dormant
uniform float uLocked;   // half-angle subtended, acquired
uniform float uBias;     // slide toward the eye, as a fraction of distance

varying vec2 vP;
varying float vAmt;
varying float vLock;

void main() {

	vec4 c = modelViewMatrix * vec4( aCenter, 1.0 );

	// Slide the whole mark a few percent of its distance back along its own view ray.
	// A uniform scale in view space is a no-op after the perspective divide, so this
	// changes nothing about WHERE the mark appears — it only lifts it clear of the
	// anchor's own machined body, which is otherwise perfectly placed to depth-reject the
	// locked ring, i.e. to hide the reticle behind the object it is marking.
	c.xyz *= uBias;

	// View space looks down -Z, so depth is -c.z. Clamped so a mark you have flown
	// straight into cannot invert or explode.
	float depth = max( - c.z, 0.35 );
	float size = depth * mix( uDormant, uLocked, aState.y );

	c.xy += position.xy * size;

	vP = position.xy;
	vAmt = aState.x;
	vLock = aState.y;

	gl_Position = projectionMatrix * c;

}
`;

const RETICLE_FRAG = /* glsl */ `
uniform vec3 uInk;
uniform vec3 uCore;
uniform float uTime;

varying vec2 vP;
varying float vAmt;
varying float vLock;

/** Axis-aligned rounded-off box mask. */
float boxMask( vec2 q, vec2 h, float aa ) {

	vec2 d = abs( q ) - h;
	return 1.0 - smoothstep( - aa, aa, max( d.x, d.y ) );

}

/**
 * Four corner brackets, arms pointing inward. "off" is how far out the corners sit, so
 * animating it from wide to tight IS the acquisition gesture.
 */
float brackets( vec2 p, float off, float len, float w, float aa ) {

	vec2 q = abs( p ) - vec2( off );
	float armX = boxMask( q + vec2( len * 0.5, 0.0 ), vec2( len * 0.5, w ), aa );
	float armY = boxMask( q + vec2( 0.0, len * 0.5 ), vec2( w, len * 0.5 ), aa );
	return max( armX, armY );

}

void main() {

	if ( vAmt <= 0.004 ) discard;

	// Dormant marks sit on the diagonal and the acquired one snaps square. That is a
	// second, rotational channel on top of the convergence, so the state change is
	// legible even when the whole mark is only thirty pixels across.
	float rot = mix( 0.7854, 0.0, vLock );
	float cr = cos( rot );
	float sr = sin( rot );
	vec2 p = mat2( cr, - sr, sr, cr ) * vP;

	float aa = fwidth( p.x ) * 0.8 + 0.001;

	// The convergence. Corners run from 0.46 of the quad to 0.24 of it, which at the sizes
	// above is roughly 25 screen pixels closing to 15 — a real, readable movement.
	float off = mix( 0.46, 0.24, vLock );
	float len = mix( 0.10, 0.17, vLock );
	float w = mix( 0.016, 0.024, vLock );
	float edge = mix( 0.016, 0.020, vLock );

	float core = brackets( p, off, len, w, aa );
	float ink = brackets( p, off, len, w + edge, aa );

	// The closing ring only exists once acquired: the brackets say "targetable", the ring
	// says "this one". Hollow, so it can never hide whatever is behind the anchor.
	float rd = abs( length( p ) - 0.17 );
	float ringCore = ( 1.0 - smoothstep( w * 0.8, w * 0.8 + aa, rd ) ) * vLock;
	float ringInk = ( 1.0 - smoothstep( w * 0.8 + edge, w * 0.8 + edge + aa, rd ) ) * vLock;
	core = max( core, ringCore );
	ink = max( ink, ringInk );

	if ( ink <= 0.004 ) discard;

	// A slow breath on the acquired mark only. Small on purpose — this is confirmation,
	// not an alarm.
	float breath = 1.0 - vLock * 0.14 * ( 0.5 + 0.5 * sin( uTime * 7.0 ) );

	float a = ink * vAmt * breath;
	if ( a <= 0.004 ) discard;

	// The crosshair's contract: a white core inside a near-black edge, so one of the two
	// always clears the surface behind it by a wide margin. Premultiplied source-over —
	// this is a drawn mark on the frame, and a sunlit atrium has no headroom to add to.
	vec3 col = mix( uInk, uCore, core );
	gl_FragColor = vec4( col * a, a );

}
`;

class AnchorReticle {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly plane: THREE.PlaneGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly centers: Float32Array;
  private readonly states: Float32Array;
  private readonly centerAttr: THREE.InstancedBufferAttribute;
  private readonly stateAttr: THREE.InstancedBufferAttribute;

  /** Smoothed lock value per anchor id, so acquisition animates instead of popping. */
  private readonly lock: Float32Array;

  constructor(anchorCount: number) {
    const cap = Math.min(ANCHOR_MARK_POOL, Math.max(1, anchorCount));
    this.centers = new Float32Array(cap * 3);
    this.states = new Float32Array(cap * 2);
    this.lock = new Float32Array(Math.max(1, anchorCount));

    this.plane = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.name = 'vfx-anchor-reticle';
    const pos = this.plane.getAttribute('position');
    geo.setAttribute('position', pos);
    geo.setIndex(this.plane.getIndex());
    this.centerAttr = new THREE.InstancedBufferAttribute(this.centers, 3);
    this.centerAttr.setUsage(THREE.DynamicDrawUsage);
    this.stateAttr = new THREE.InstancedBufferAttribute(this.states, 2);
    this.stateAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aCenter', this.centerAttr);
    geo.setAttribute('aState', this.stateAttr);
    geo.instanceCount = 0;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    const mat = new THREE.ShaderMaterial({
      name: 'vfx-anchor-reticle',
      uniforms: {
        uInk: { value: new THREE.Color().setHex(VFX.ink, THREE.SRGBColorSpace) },
        uCore: { value: new THREE.Color(1, 1, 1) },
        uTime: { value: 0 },
        uDormant: { value: 0.05 },
        uLocked: { value: 0.058 },
        uBias: { value: ANCHOR_DEPTH_BIAS },
      },
      vertexShader: RETICLE_VERT,
      fragmentShader: RETICLE_FRAG,
      // Depth-tested so a wall hides the mark behind it — honest, and a free stand-in for
      // the sim's line-of-sight test. Never written to, so it cannot occlude anything.
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    applyVfxBlend(mat, 'over');

    this.geometry = geo;
    this.material = mat;
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.name = 'vfx-anchor-reticle';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = 24;
    this.mesh.visible = false;
  }

  hide(): void {
    this.mesh.visible = false;
    this.geometry.instanceCount = 0;
  }

  /**
   * Rebuild the instance list for this frame.
   *
   * `bestId` is the anchor that would actually be hooked; `-1` means none. Everything
   * else is a fade computed from how far off the crosshair the anchor is and how far
   * away it is, so the marks brighten as the player turns toward them — which is the
   * behaviour that teaches the aim cone without a word of copy.
   */
  update(
    dt: number,
    anchors: GrappleAnchor[],
    ex: number,
    ey: number,
    ez: number,
    dx: number,
    dy: number,
    dz: number,
    bestId: number,
    ready: number,
    time: number,
    fovDeg: number,
  ): void {
    const cap = this.states.length >> 1;
    const coneIn = GRAPPLE_AIM_CONE * ANCHOR_FADE_CONE_IN;
    const coneOut = GRAPPLE_AIM_CONE * ANCHOR_FADE_CONE_OUT;
    const distIn = GRAPPLE_RANGE * ANCHOR_FADE_DIST;
    const dim = ANCHOR_COOLING_DIM + (1 - ANCHOR_COOLING_DIM) * ready;

    let n = 0;
    for (let i = 0; i < anchors.length; i++) {
      const a = anchors[i]!;
      const id = a.id;
      const slot = id >= 0 && id < this.lock.length ? id : -1;

      const vx = a.pos.x - ex;
      const vy = a.pos.y - ey;
      const vz = a.pos.z - ez;
      const d = Math.sqrt(vx * vx + vy * vy + vz * vz);

      let amt = 0;
      if (d > 1e-3 && d <= GRAPPLE_RANGE) {
        const cos = (vx * dx + vy * dy + vz * dz) / d;
        const ang = Math.acos(cos < -1 ? -1 : cos > 1 ? 1 : cos);
        amt = 1 - smooth01(coneIn, coneOut, ang);
        amt *= 1 - smooth01(distIn, GRAPPLE_RANGE, d);
      }

      const target = id === bestId ? 1 : 0;
      let lk = slot >= 0 ? this.lock[slot] : target;
      const rate = target > lk ? ANCHOR_LOCK_RATE_ON : ANCHOR_LOCK_RATE_OFF;
      lk += (target - lk) * (1 - Math.exp(-rate * dt));
      if (lk < 0.002) lk = 0;
      if (slot >= 0) this.lock[slot] = lk;

      // The acquired anchor is always fully drawn, even at the very edge of the cone:
      // the whole point of the mark is to answer "what will E do right now".
      const alpha = Math.max(ANCHOR_DORMANT_ALPHA * amt * dim, lk);
      if (alpha < ANCHOR_MIN_ALPHA || n >= cap) continue;

      this.centers[n * 3] = a.pos.x;
      this.centers[n * 3 + 1] = a.pos.y;
      this.centers[n * 3 + 2] = a.pos.z;
      this.states[n * 2] = alpha;
      this.states[n * 2 + 1] = lk;
      n++;
    }

    this.geometry.instanceCount = n;
    this.mesh.visible = n > 0;
    if (n === 0) return;

    // A fraction of frame height is a fraction of `2 * tan(fov/2)` at unit depth, so the
    // mark holds the same size on screen through the whole FOV push at speed.
    const k = 2 * Math.tan((fovDeg * Math.PI) / 360);
    this.material.uniforms.uDormant.value = k * ANCHOR_SIZE_DORMANT;
    this.material.uniforms.uLocked.value = k * ANCHOR_SIZE_LOCKED;
    this.material.uniforms.uTime.value = time;
    this.centerAttr.clearUpdateRanges();
    this.centerAttr.addUpdateRange(0, n * 3);
    this.centerAttr.needsUpdate = true;
    this.stateAttr.clearUpdateRanges();
    this.stateAttr.addUpdateRange(0, n * 2);
    this.stateAttr.needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    this.plane.dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** GLSL `smoothstep`, on the CPU. */
function smooth01(edge0: number, edge1: number, x: number): number {
  if (edge1 <= edge0) return x < edge0 ? 0 : 1;
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

const _d: Vec3 = v3();
const _aim: Vec3 = v3();
const _r: Vec3 = v3();
const _muzzle: Vec3 = v3();
const _size = new THREE.Vector2();

function teamColor(team: number): number {
  return TEAM_COLORS[team === 1 ? 1 : 0];
}

function eyeY(p: PlayerState): number {
  return p.pos.y + (p.crouching ? PLAYER_CROUCH_EYE_HEIGHT : PLAYER_EYE_HEIGHT);
}

/**
 * Which anchor the grapple would take, from a given eye and aim direction.
 *
 * Mirrors `pickAnchor` in @shared/sim/movement — same cone, same range, same
 * nearest-to-the-crosshair tie-break — with ONE deliberate omission: the line-of-sight
 * raycast, because the renderer has no collision world and must not acquire one. The
 * mark that reads this is depth-tested, so an anchor behind a wall is not drawn anyway;
 * the only residual case is an anchor the sim would refuse that this still highlights,
 * which is a missing bracket at worst, never a wrong grapple.
 */
function pickAnchorForView(
  anchors: GrappleAnchor[],
  ex: number,
  ey: number,
  ez: number,
  dx: number,
  dy: number,
  dz: number,
): number {
  let bestId = -1;
  let bestAngle = GRAPPLE_AIM_CONE;
  let bestDist = Infinity;
  for (let i = 0; i < anchors.length; i++) {
    const a = anchors[i]!;
    const vx = a.pos.x - ex;
    const vy = a.pos.y - ey;
    const vz = a.pos.z - ez;
    const d = Math.sqrt(vx * vx + vy * vy + vz * vz);
    if (d > GRAPPLE_RANGE || d < 1e-4) continue;
    const c = (vx * dx + vy * dy + vz * dz) / d;
    const ang = Math.acos(c < -1 ? -1 : c > 1 ? 1 : c);
    if (ang > bestAngle || (ang === bestAngle && d >= bestDist)) continue;
    bestAngle = ang;
    bestDist = d;
    bestId = a.id;
  }
  return bestId;
}

/** Where a shot, throw or flash visually originates. Offset so it never sits on the lens. */
function muzzleOf(p: PlayerState, isLocal: boolean, out: Vec3): Vec3 {
  dirFromYawPitch(_d, p.yaw, p.pitch);
  rightFromYaw(_r, p.yaw);
  const fwd = isLocal ? 0.95 : 0.5;
  const side = isLocal ? 0.26 : 0.18;
  const drop = isLocal ? 0.22 : 0.1;
  out.x = p.pos.x + _d.x * fwd + _r.x * side;
  out.y = eyeY(p) + _d.y * fwd - drop;
  out.z = p.pos.z + _d.z * fwd + _r.z * side;
  return out;
}

/**
 * Where a hand-anchored burst is drawn.
 *
 * Identical to {@link muzzleOf} for a remote player. For the LOCAL player it is pushed
 * a further `LOCAL_HAND_PUSH` down the view direction, which — together with the paired
 * `LOCAL_HAND_SCALE` on every size — is what stops a two-metre snap ring emitted at
 * arm's length from becoming a two-metre snap ring across the crosshair.
 */
function handOrigin(p: PlayerState, isLocal: boolean, out: Vec3): Vec3 {
  muzzleOf(p, isLocal, out);
  if (isLocal) {
    dirFromYawPitch(_d, p.yaw, p.pitch);
    out.x += _d.x * LOCAL_HAND_PUSH;
    out.y += _d.y * LOCAL_HAND_PUSH;
    out.z += _d.z * LOCAL_HAND_PUSH;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Screen-space overlay quad
// ---------------------------------------------------------------------------

class Overlay {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.ShaderMaterial;

  constructor(
    geometry: THREE.PlaneGeometry,
    fragment: string,
    uniforms: { [k: string]: THREE.IUniform },
    blend: 'add' | 'over',
    renderOrder: number,
  ) {
    const mat = new THREE.ShaderMaterial({
      name: 'vfx-overlay',
      uniforms,
      vertexShader: OVERLAY_VERT,
      fragmentShader: fragment,
      depthTest: false,
      toneMapped: false,
      side: THREE.FrontSide,
    });
    applyVfxBlend(mat, blend);
    this.material = mat;
    this.mesh = new THREE.Mesh(geometry, mat);
    this.mesh.name = 'vfx-overlay';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.visible = false;
    this.mesh.position.set(0, 0, -OVERLAY_DIST);
  }

  /** Resize to exactly fill the frustum at OVERLAY_DIST for the camera's current FOV. */
  fit(camera: THREE.PerspectiveCamera): void {
    const h = 2 * Math.tan((camera.fov * Math.PI) / 360) * OVERLAY_DIST;
    const w = h * camera.aspect;
    this.mesh.scale.set(w * 1.02, h * 1.02, 1);
    const u = this.material.uniforms.uAspect;
    if (u !== undefined) u.value = camera.aspect;
  }

  dispose(): void {
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// VfxSystem
// ---------------------------------------------------------------------------

export interface VfxHooks {
  /** Camera trauma in 0..1. Wire to `Renderer.addShake`. */
  shake?: (amount: number) => void;
  /** Two-frame full-screen ink splat. Wire to `Renderer.inkSplash`. */
  inkSplash?: (colorHex: number) => void;
}

interface ShockwaveSlot {
  mesh: THREE.Mesh;
  material: THREE.ShaderMaterial;
  age: number;
  live: boolean;
  radius: number;
  /** Radius this shell coasts out to. Smaller for the local caster's own pulse. */
  reach: number;
}

interface BeaconSlot {
  group: THREE.Group;
  material: THREE.ShaderMaterial;
}

interface BurstSlot {
  overlay: Overlay;
  age: number;
  duration: number;
  live: boolean;
}

export class VfxSystem {
  /** Sub-systems are public so a host can tune caps or reuse them for the menu backdrop. */
  readonly particles: ParticleSystem;
  readonly trails: TrailSystem;
  readonly impacts: ImpactSystem;

  /** Optional bridges to the Renderer's feel channels. Everything works without them. */
  hooks: VfxHooks = {};

  private readonly scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera | null = null;

  private readonly overlayGeometry: THREE.PlaneGeometry;
  private readonly wound: Overlay;
  private readonly noon: Overlay;
  private readonly bursts: BurstSlot[] = [];

  private readonly shockGeometry: THREE.IcosahedronGeometry;
  private readonly shocks: ShockwaveSlot[] = [];
  private readonly beacons: BeaconSlot[] = [];
  private readonly beaconGeometry: THREE.PlaneGeometry;

  /**
   * The grapple affordance. See the ANCHOR_* block above for why it exists.
   *
   * The anchor list comes from `ARENA` rather than from `WorldState`, because a snapshot
   * carries entities and not geometry. There is exactly one arena in this game and the
   * server ships that same table; if a second one is ever added, this needs a `setArena`
   * the way the arena mesh has one.
   */
  private readonly reticle: AnchorReticle;
  private readonly anchors: GrappleAnchor[] = ARENA.anchors;

  private sceneTexture: THREE.FramebufferTexture | null = null;
  private grabW = 0;
  private grabH = 0;
  private grabSupported = true;
  private grabChecked = false;
  private grabFrame = -1;
  private frame = 0;
  private idleGrabTime = 0;

  private readonly woundMarkerT = new Map<string, number>();
  private time = 0;
  private dpr = 1;
  private woundAmount = 0;
  private woundPulse = 0;
  private wasWounded = false;
  private noonAmount = 0;
  private disposed = false;

  /** The local player's eye, refreshed every frame. Drives {@link capSolid}. */
  private readonly viewer: Vec3 = v3();
  private hasViewer = false;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    this.particles = new ParticleSystem(scene);
    this.trails = new TrailSystem(scene, this.particles);
    this.impacts = new ImpactSystem(scene, this.particles);

    // --- screen-space overlays (parented to the camera once we find it)
    this.overlayGeometry = new THREE.PlaneGeometry(1, 1);

    this.wound = new Overlay(
      this.overlayGeometry,
      WOUND_FRAG,
      {
        uSteady: { value: 0 },
        uPulse: { value: 0 },
        uTime: { value: 0 },
        uAspect: { value: 1 },
        uRim: { value: new THREE.Color().setHex(VFX.dangerDeep, THREE.SRGBColorSpace) },
        uHot: { value: new THREE.Color().setHex(VFX.danger, THREE.SRGBColorSpace) },
      },
      'over',
      3000,
    );

    this.noon = new Overlay(
      this.overlayGeometry,
      NOON_FRAG,
      {
        uAmount: { value: 0 },
        uTime: { value: 0 },
        uAspect: { value: 1 },
        // Neutral slate, NOT PALETTE.loaded. Amber belongs to the chambered gun and nothing
        // else may compete with it — least of all a full-border overlay during sudden death.
        uColor: { value: new THREE.Color().setHex(PALETTE.ink, THREE.SRGBColorSpace) },
      },
      'over',
      3001,
    );

    for (let i = 0; i < BURST_OVERLAYS; i++) {
      const overlay = new Overlay(
        this.overlayGeometry,
        BURST_FRAG,
        {
          uKind: { value: 0 },
          uProgress: { value: 1 },
          uAspect: { value: 1 },
          uSeed: { value: 0 },
          uOpacity: { value: 1 },
          uColor: { value: new THREE.Color(1, 1, 1) },
        },
        'over',
        3002 + i,
      );
      this.bursts.push({ overlay, age: 0, duration: 1, live: false });
    }

    // --- shockwave shells
    this.shockGeometry = new THREE.IcosahedronGeometry(1, 3);
    for (let i = 0; i < SHOCKWAVE_POOL; i++) {
      const mat = new THREE.ShaderMaterial({
        name: 'vfx-shockwave',
        uniforms: {
          uScene: { value: null },
          uResolution: { value: new THREE.Vector2(1, 1) },
          uHasScene: { value: 0 },
          uProgress: { value: 1 },
          uStrength: { value: SHOCKWAVE_REFRACT },
          uColor: { value: new THREE.Color().setHex(PALETTE.neutral, THREE.SRGBColorSpace) },
        },
        vertexShader: SHOCK_VERT,
        fragmentShader: SHOCK_FRAG,
        side: THREE.DoubleSide,
        depthTest: true,
        toneMapped: false,
      });
      // Deliberately NOT applyVfxBlend: the shell replaces the pixel it refracts, but it
      // must leave the alpha channel alone so it never punches a hole in the bloom mask
      // of whatever is glowing behind it.
      mat.transparent = true;
      mat.depthWrite = false;
      mat.blending = THREE.CustomBlending;
      mat.blendEquation = THREE.AddEquation;
      mat.blendSrc = THREE.OneFactor;
      mat.blendDst = THREE.OneMinusSrcAlphaFactor;
      mat.blendEquationAlpha = THREE.AddEquation;
      mat.blendSrcAlpha = THREE.ZeroFactor;
      mat.blendDstAlpha = THREE.OneFactor;

      const mesh = new THREE.Mesh(this.shockGeometry, mat);
      mesh.name = 'vfx-shockwave';
      mesh.frustumCulled = false;
      mesh.visible = false;
      mesh.renderOrder = 40;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.onBeforeRender = (renderer): void => {
        this.grabScene(renderer, mat);
      };
      scene.add(mesh);
      this.shocks.push({
        mesh,
        material: mat,
        age: 0,
        live: false,
        radius: 1,
        reach: PULSE_RANGE * SHOCKWAVE_REACH,
      });
    }

    // --- sudden-death beacons
    this.beaconGeometry = new THREE.PlaneGeometry(1, 1);
    this.beaconGeometry.translate(0, 0.5, 0);
    for (let i = 0; i < BEACON_POOL; i++) {
      const mat = new THREE.ShaderMaterial({
        name: 'vfx-beacon',
        uniforms: {
          uColor: { value: new THREE.Color(1, 1, 1) },
          uTime: { value: 0 },
          uAmount: { value: 1 },
          uPeak: { value: BEACON_PEAK_ALPHA },
        },
        vertexShader: BEACON_VERT,
        fragmentShader: BEACON_FRAG,
        // Through geometry, by design: at HIGH NOON nobody's position is a secret.
        depthTest: false,
        side: THREE.DoubleSide,
        toneMapped: false,
      });
      applyVfxBlend(mat, 'over');

      const group = new THREE.Group();
      group.name = 'vfx-beacon';
      group.visible = false;
      for (let k = 0; k < BEACON_PLANES; k++) {
        const plane = new THREE.Mesh(this.beaconGeometry, mat);
        plane.rotation.y = (k / BEACON_PLANES) * Math.PI;
        plane.scale.set(BEACON_WIDTH, BEACON_HEIGHT, 1);
        plane.frustumCulled = false;
        plane.renderOrder = 2500;
        plane.castShadow = false;
        plane.receiveShadow = false;
        group.add(plane);
      }
      scene.add(group);
      this.beacons.push({ group, material: mat });
    }

    // --- grapple anchor reticle
    this.reticle = new AnchorReticle(this.anchors.length);
    scene.add(this.reticle.mesh);
  }

  // -------------------------------------------------------------------------
  // Event mapping
  // -------------------------------------------------------------------------

  handle(e: GameEvent, w: WorldState, localId: string): void {
    if (this.disposed) return;
    // Events are dispatched before the first `update`, so the viewer is refreshed here as
    // well: a solid shape spawned on frame one must be clamped on frame one.
    this.syncViewer(w, localId);
    switch (e.t) {
      case 'shot':
        this.onShot(e, w, localId);
        break;
      case 'dryfire':
        this.onDryFire(e.player, w, localId);
        break;
      case 'kill':
        this.onKill(e.killer, e.victim, e.zone, w, localId);
        break;
      case 'wound':
        this.onWound(e.victim, w, localId);
        break;
      case 'heal':
        this.onHeal(e.player, w, localId);
        break;
      case 'throw':
        this.onThrow(e.player, e.gunId, e.charged, w, localId);
        break;
      case 'catch':
        this.onCatch(e.player, e.chambered, e.intercept, w, localId);
        break;
      case 'pickup':
        this.onPickup(e.player, w);
        break;
      case 'gunbounce':
        this.impacts.dustPuff(
          e.pos.x,
          e.pos.y,
          e.pos.z,
          0,
          1,
          0,
          clamp01(e.speed / 22),
          e.surface,
        );
        break;
      case 'gunreset':
        this.onGunReset(e.gunId, w);
        break;
      case 'pulse':
        this.onPulse(e.player, e.pos, e.hitPlayers.length > 0 || e.hitGun >= 0, w, localId);
        break;
      case 'disarm':
        this.onDisarm(e.player, e.by, w, localId);
        break;
      case 'jump':
        this.onJump(e.player, e.double, w, localId);
        break;
      case 'land':
        this.onLand(e.player, e.speed, e.surface, w);
        break;
      case 'slidestart':
        this.onSlide(e.player, w);
        break;
      case 'dash':
        this.onDash(e.player, e.dir, w, localId);
        break;
      case 'wallrun':
        if (e.start) this.onWallrunStart(e.player, w);
        break;
      case 'walljump':
        this.onWallJump(e.player, w);
        break;
      case 'grapplefire':
        this.onGrappleFire(e.player, w, localId);
        break;
      case 'grappleattach':
        this.onGrappleAttach(e.player, w);
        break;
      case 'grapplerelease':
        this.onGrappleRelease(e.player, e.boosted, w, localId);
        break;
      case 'roundstart':
        this.onRoundStart();
        break;
      case 'roundend':
        this.onRoundEnd(e.winner, w, localId);
        break;
      case 'suddendeath':
        this.onSuddenDeath();
        break;
      case 'matchend':
        this.onMatchEnd(e.winner, w, localId);
        break;
      case 'playerjoin':
      case 'playerleave':
        // Lobby-level bookkeeping. The UI owns the feedback for these; there is no
        // world-space effect because there is no world position to attach one to.
        break;
      default:
        break;
    }
  }

  // --- the shot -------------------------------------------------------------

  private onShot(
    e: Extract<GameEvent, { t: 'shot' }>,
    w: WorldState,
    localId: string,
  ): void {
    const shooter = w.players[e.player];
    const isLocal = e.player === localId;
    const team = shooter !== undefined ? shooter.team : 0;

    // Origin: the eye for the trace, the muzzle for the picture.
    let ox = e.origin.x;
    let oy = e.origin.y;
    let oz = e.origin.z;
    if (shooter !== undefined) {
      muzzleOf(shooter, isLocal, _muzzle);
      ox = _muzzle.x;
      oy = _muzzle.y;
      oz = _muzzle.z;
    }

    const headshot = e.zone === HitZone.Head && e.hitPlayer !== null;
    this.impacts.tracer(ox, oy, oz, e.hitPoint.x, e.hitPoint.y, e.hitPoint.z, team, headshot);
    this.impacts.muzzleFlash(ox, oy, oz, e.dir.x, e.dir.y, e.dir.z, team, isLocal);

    if (e.hitPlayer !== null) {
      const victim = w.players[e.hitPlayer];
      const vt = victim !== undefined ? victim.team : team === 1 ? 0 : 1;
      if (headshot) {
        this.impacts.headshot(e.hitPoint.x, e.hitPoint.y, e.hitPoint.z, e.dir.x, e.dir.y, e.dir.z, team);
        if (isLocal || e.hitPlayer === localId) {
          this.burst(BURST_HEADSHOT, isLocal ? PALETTE.loaded : teamColor(team));
        }
        // Under-shaken on purpose. A headshot is followed immediately by the kill event,
        // which is where the weight is spent — and the instant after landing one is
        // exactly when you need to see the rest of their team.
        if (isLocal) this.hooks.shake?.(SHAKE_MY_HEADSHOT);
      } else {
        this.impacts.bodyHit(e.hitPoint.x, e.hitPoint.y, e.hitPoint.z, e.dir.x, e.dir.y, e.dir.z, vt);
        if (isLocal) this.hooks.shake?.(SHAKE_MY_BODYSHOT);
      }
    } else {
      // No surface normal travels with the event, so the burst is oriented back along the
      // shot. That is exact for a square-on hit and acceptable for a glancing one.
      this.impacts.worldImpact(
        e.hitPoint.x,
        e.hitPoint.y,
        e.hitPoint.z,
        -e.dir.x,
        -e.dir.y,
        -e.dir.z,
        SurfaceKind.Concrete,
        1,
      );
      if (isLocal) this.hooks.shake?.(SHAKE_MY_MISS);
    }
  }

  /**
   * The empty click — the sound of death, and its picture.
   *
   * This is a *non-event*, and the whole trick is that it has to be a clearly visible
   * one. Everything else in this game snaps open; this collapses. Everything else is hot
   * amber or team colour; this is dead slate, the only place in the vocabulary that
   * colour is deliberately withheld. And it is entirely matte, so on a white wall the
   * player sees a small dark shape fold in on itself and drop: a gesture of giving up,
   * not an absence of feedback.
   */
  private onDryFire(playerId: string, w: WorldState, localId: string): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const isLocal = playerId === localId;
    handOrigin(p, isLocal, _muzzle);
    const ps = this.particles;
    const k = isLocal ? LOCAL_HAND_SCALE : 1;

    // A star that COLLAPSES instead of expanding — the anti-muzzle-flash. Ink, so it is
    // unmistakably a dark mark on a bright world rather than a missing highlight.
    let d = ps.begin(P_STAR, false);
    d.count = 1;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.life = 0.22;
    d.lifeVar = 0;
    d.size0 = this.capSolid(0.55 * k, _muzzle.x, _muzzle.y, _muzzle.z);
    d.size1 = 0.0;
    d.sizeVar = 0;
    // Slate going to INK as it shrinks. Two things are load-bearing here. It gets DARKER
    // as it collapses, which is what makes it read as a failure rather than as a fade;
    // and it starts dark, because the shape is at its largest on the first frame and
    // starting at `spent` grey spent that frame — the widest, most legible instant of the
    // whole effect — on a value only a shade off the wall behind it.
    d.color0 = VFX.debris;
    d.color1 = VFX.ink;
    d.vy = -0.5;
    d.gravity = -2.4;
    d.drag = 8;
    d.priority = 2;
    ps.emit();

    // A ring that runs INWARD. Every other ring in the game expands, so a contracting one
    // is unambiguous even at the edge of vision — and it gives the click a hard drawn
    // silhouette that survives being seen against a sunlit wall, which is the whole
    // problem with rendering an absence.
    d = ps.begin(P_RING, false);
    d.count = 1;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.life = 0.26;
    d.lifeVar = 0;
    d.size0 = this.capSolid(0.78 * k, _muzzle.x, _muzzle.y, _muzzle.z);
    d.size1 = 0.04 * k;
    d.sizeVar = 0;
    d.color0 = VFX.spent;
    d.color1 = VFX.ink;
    d.vy = -0.35;
    d.gravity = -2.0;
    d.drag = 5;
    d.priority = 2;
    ps.emit();

    // A puff that SHRINKS and sinks. Grey going darker: the smoke of nothing happening.
    // Every other puff in the game blooms outward; this is the only one that deflates,
    // and it is given enough mass and enough drop to be unmistakably a sag.
    d = ps.begin(P_PUFF, false);
    d.count = 5;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.posJitter = 0.05;
    d.vx = 0;
    d.vy = -0.6;
    d.vz = 0;
    d.velJitter = 0.25;
    d.gravity = -2.2;
    d.drag = 4.5;
    d.life = 0.6;
    d.lifeVar = 0.25;
    d.size0 = 0.42 * k;
    d.size1 = 0.05 * k;
    d.color0 = VFX.dustDeep;
    d.color1 = VFX.ink;
    d.spinRate = 0.8;
    d.spinVar = 1.4;
    d.priority = 2;
    ps.emit();

    // Three sad little specks that fall straight out of the barrel and are not caught by
    // anything. The one piece of debris in the game with no energy behind it.
    d = ps.begin(P_DOT, false);
    d.count = 5;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.posJitter = 0.04;
    d.vy = -0.2;
    d.velJitter = 0.5;
    d.gravity = -14;
    d.drag = 0.3;
    d.life = 0.7;
    d.lifeVar = 0.3;
    d.size0 = 0.045 * k;
    d.size1 = 0.03 * k;
    d.color0 = VFX.ink;
    d.color1 = VFX.ink;
    d.spinRate = 5;
    d.spinVar = 5;
    d.priority = 2;
    ps.emit();

    // The smallest kick in the game, and only for the person it happened to. A dry fire
    // is a non-event; the camera should barely acknowledge it.
    if (isLocal) this.hooks.shake?.(SHAKE_DRYFIRE);
  }

  private onKill(
    killerId: string,
    victimId: string,
    zone: HitZone,
    w: WorldState,
    localId: string,
  ): void {
    const victim = w.players[victimId];
    const killer = w.players[killerId];
    const kTeam = killer !== undefined ? killer.team : 0;
    const vTeam = victim !== undefined ? victim.team : kTeam === 1 ? 0 : 1;

    if (victim !== undefined) {
      const cx = victim.pos.x;
      const cy = victim.pos.y + PLAYER_HEIGHT * 0.55;
      const cz = victim.pos.z;
      const ps = this.particles;

      // A burst that scatters like a body coming apart into flat shapes: this is a comic
      // death, not a gore effect.
      let d = ps.begin(P_INK, false);
      d.count = 40;
      d.px = cx;
      d.py = cy;
      d.pz = cz;
      d.posJitter = 0.35;
      d.vy = 4.5;
      d.velJitter = 7.5;
      d.gravity = -21;
      d.drag = 0.9;
      d.life = 1.5;
      d.lifeVar = 0.5;
      d.size0 = 0.30;
      d.size1 = 0.09;
      d.color0 = teamColor(vTeam);
      d.color1 = VFX.ink;
      d.spinRate = 6;
      d.spinVar = 8;
      d.priority = 3;
      ps.emit();

      // Matte shock rings in the KILLER's colour. Opaque and hollow: you see straight
      // through them to whoever else is in the room, and they still read as a hard
      // graphic against white plaster.
      d = ps.begin(P_RING, false);
      d.count = 2;
      d.px = cx;
      d.py = cy;
      d.pz = cz;
      d.delayVar = 0.07;
      d.life = 0.36;
      d.lifeVar = 0.1;
      d.size0 = 0.4;
      d.size1 = 4.0;
      d.sizeVar = 0.15;
      d.color0 = teamColor(kTeam);
      d.color1 = teamColor(kTeam);
      d.drag = 3.2;
      d.priority = 3;
      ps.emit();

      d = ps.begin(P_SPARK, true);
      d.count = 22;
      d.px = cx;
      d.py = cy;
      d.pz = cz;
      d.posJitter = 0.2;
      d.vy = 1.5;
      d.velJitter = 10;
      d.gravity = -16;
      d.drag = 2.4;
      d.life = 0.5;
      d.lifeVar = 0.5;
      d.size0 = 0.22;
      d.size1 = 0.01;
      d.color0 = VFX.core;
      d.color1 = teamColor(kTeam);
      d.intensity0 = 5;
      d.intensity1 = 1.2;
      d.bloom = 1;
      d.stretch = 1.5;
      d.priority = 3;
      ps.emit();

      // Ink hits the floor under the victim, in their own colour: the arena keeps a
      // record of where the round was decided. The bite through the middle is ink, not
      // white — a white crater on a white floor is a hole in the decal, not a highlight.
      this.impacts.splat(
        cx,
        victim.pos.y + 0.02,
        cz,
        0,
        1,
        0,
        zone === HitZone.Head ? KILL_SPLAT_HEAD : KILL_SPLAT_BODY,
        teamColor(vTeam),
        VFX.ink,
      );
    }

    this.woundMarkerT.delete(victimId);

    // DESIGN.md 4: a two-frame full-screen ink splash in the KILLER's colour.
    //
    // Gated on being in the fight. The splash is a full-screen impact frame; firing it
    // for a kill on the far side of the arena would make it constant noise and rob it of
    // the meaning it has when the kill is yours. The world-space ink above still records
    // every kill for everyone.
    const involved = killerId === localId || victimId === localId;
    if (involved) {
      this.hooks.inkSplash?.(teamColor(kTeam));
      this.burst(BURST_KILL, teamColor(kTeam));
      // Dying is the loudest thing that happens to you. Killing is not: the shot hook
      // has already fired this frame, and stacking a second big number on top of it was
      // what took the camera to saturation on every headshot.
      this.hooks.shake?.(victimId === localId ? SHAKE_DEATH : SHAKE_KILL_NEARBY);
    }
  }

  private onWound(victimId: string, w: WorldState, localId: string): void {
    const p = w.players[victimId];
    if (p === undefined) return;
    const ps = this.particles;
    const cy = p.pos.y + PLAYER_HEIGHT * 0.6;

    const d = ps.begin(P_INK, false);
    d.count = 18;
    d.px = p.pos.x;
    d.py = cy;
    d.pz = p.pos.z;
    d.posJitter = 0.22;
    d.vy = 2.2;
    d.velJitter = 4.5;
    d.gravity = -20;
    d.drag = 1.0;
    d.life = 1.0;
    d.lifeVar = 0.4;
    d.size0 = 0.18;
    d.size1 = 0.06;
    d.color0 = VFX.danger;
    d.color1 = VFX.dangerDeep;
    d.spinRate = 6;
    d.spinVar = 7;
    d.priority = 2;
    ps.emit();

    this.woundMarkerT.set(victimId, 0);
    if (victimId === localId) {
      // One thump of the rim, and no wash: the frame you are hit on is the frame you
      // most need to be able to read.
      this.woundPulse = 1;
      this.hooks.shake?.(SHAKE_WOUNDED);
    }
  }

  /**
   * The heal: a restorative column that rises instead of scattering.
   *
   * Amber going to team colour, because that is literally what happened — the chamber
   * was spent to put you back on your feet. It is the same amber as the gun, used at the
   * one other moment a round is consumed, and it costs the palette nothing new.
   */
  private onHeal(playerId: string, w: WorldState, localId: string): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const ps = this.particles;
    const base = p.pos.y + 0.05;
    const isLocal = playerId === localId;

    const team = teamColor(p.team);

    // Matte rings that CONTRACT around the player as they come back up.
    let d = ps.begin(P_RING, false);
    d.count = 3;
    d.px = p.pos.x;
    d.py = base;
    d.pz = p.pos.z;
    d.vy = 3.1;
    d.delayVar = 0.16;
    d.life = 0.62;
    d.lifeVar = 0.1;
    d.size0 = 1.9;
    d.size1 = 0.5;
    d.sizeVar = 0.1;
    d.color0 = VFX.hot;
    d.color1 = team;
    d.drag = 1.2;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_DOT, false);
    d.count = 34;
    d.px = p.pos.x;
    d.py = base;
    d.pz = p.pos.z;
    d.posJitter = 0.55;
    d.vy = 3.4;
    d.speedVar = 0.5;
    d.velJitter = 0.7;
    d.gravity = 1.6;
    d.drag = 1.1;
    d.life = 0.85;
    d.lifeVar = 0.4;
    d.delayVar = 0.2;
    d.size0 = 0.08;
    d.size1 = 0.02;
    d.color0 = VFX.hot;
    d.color1 = team;
    d.priority = 3;
    ps.emit();

    // The chest flash is the third-person read: it is how a teammate across the atrium
    // knows a heal just happened. Anchored on your own body it would be a screen-wide
    // white-out, and the wound overlay lifting off the frame already tells you.
    if (isLocal) return;

    const chestY = p.pos.y + PLAYER_HEIGHT * 0.6;
    const healStar = this.capSolid(2.4, p.pos.x, chestY, p.pos.z);
    d = ps.begin(P_STAR, true);
    d.count = 1;
    d.px = p.pos.x;
    d.py = chestY;
    d.pz = p.pos.z;
    d.life = 0.3;
    d.lifeVar = 0;
    d.size0 = 0.2 * (healStar / 2.4);
    d.size1 = healStar;
    d.sizeVar = 0;
    d.color0 = VFX.core;
    d.color1 = VFX.hot;
    d.intensity0 = 5;
    d.intensity1 = 0.6;
    d.bloom = 1;
    d.spinRate = 1.5;
    d.drag = 5;
    d.priority = 3;
    ps.emit();
  }

  private onThrow(
    playerId: string,
    gunId: number,
    charged: boolean,
    w: WorldState,
    localId: string,
  ): void {
    const p = w.players[playerId];
    const gun = this.gunById(w, gunId);
    if (gun !== null) this.trails.gunThrown(gun);
    if (p === undefined) return;

    const isLocal = playerId === localId;
    handOrigin(p, isLocal, _muzzle);
    dirFromYawPitch(_d, p.yaw, p.pitch);
    const ps = this.particles;
    const chambered = gun !== null && gun.chambered;
    const k = isLocal ? LOCAL_HAND_SCALE : 1;
    // The release collar states the pass's cargo at the instant it leaves the hand, in
    // the same amber-or-team language the arc itself then carries across the arena.
    const cargo = chambered ? VFX.hot : teamColor(p.team);

    const d = ps.begin(P_RING, false);
    d.count = 1;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.vx = _d.x * 3;
    d.vy = _d.y * 3;
    d.vz = _d.z * 3;
    d.life = charged ? 0.3 : 0.2;
    d.lifeVar = 0;
    d.size0 = 0.25 * k;
    d.size1 = (charged ? 2.2 : 1.3) * k;
    d.sizeVar = 0;
    d.color0 = cargo;
    d.color1 = cargo;
    d.drag = 4;
    d.priority = 2;
    ps.emit();

    if (charged) {
      const s = ps.begin(P_SPARK, true);
      s.count = 12;
      s.px = _muzzle.x;
      s.py = _muzzle.y;
      s.pz = _muzzle.z;
      s.posJitter = 0.08;
      s.vx = _d.x * 9;
      s.vy = _d.y * 9;
      s.vz = _d.z * 9;
      s.spread = 0.5;
      s.speedVar = 0.6;
      s.gravity = -8;
      s.drag = 4;
      s.life = 0.25;
      s.lifeVar = 0.4;
      s.size0 = 0.14 * k;
      s.size1 = 0.01;
      s.color0 = VFX.core;
      s.color1 = cargo;
      s.intensity0 = 3.5;
      s.intensity1 = 1;
      s.bloom = 1;
      s.stretch = 1.2;
      s.priority = 2;
      ps.emit();
    }
  }

  /** The reward beat: a radial snap-ring plus, when it chambers, a hard ignition flash. */
  private onCatch(
    playerId: string,
    chambered: boolean,
    intercept: boolean,
    w: WorldState,
    localId: string,
  ): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const isLocal = playerId === localId;
    handOrigin(p, isLocal, _muzzle);
    const ps = this.particles;
    const team = teamColor(p.team);
    const k = isLocal ? LOCAL_HAND_SCALE : 1;

    // Snap ring — always, catch or steal. Matte, and it CONTRACTS onto the hands: every
    // other ring in the game expands, so a catch cannot be mistaken for anything else
    // even at the edge of vision.
    let d = ps.begin(P_RING, false);
    d.count = 2;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.delayVar = 0.05;
    d.life = 0.28;
    d.lifeVar = 0.08;
    d.size0 = 2.0 * k;
    d.size1 = 0.28 * k;
    d.sizeVar = 0.1;
    // An interception is a theft, and theft is danger — it is the one catch that reads
    // red rather than in the catcher's colour.
    d.color0 = intercept ? VFX.danger : team;
    d.color1 = intercept ? VFX.dangerDeep : team;
    d.drag = 2.5;
    d.priority = 3;
    ps.emit();

    if (chambered) {
      // CHAMBER IGNITION. The single most valuable thing that can happen to a player,
      // so it gets the full treatment: a matte amber star that snaps open, the white
      // core inside it, and a shower of embers. The star is matte first for the same
      // reason the gun body is dark — the glow needs something to be a glow *on*.
      // Solid, so it is capped against how close the hands are. A teammate chambering a
      // catch at arm's length used to put a two-metre opaque star over the whole frame.
      const bodyStar = this.capSolid(1.9 * k, _muzzle.x, _muzzle.y, _muzzle.z);
      const lightStar = this.capSolid(1.4 * k, _muzzle.x, _muzzle.y, _muzzle.z);

      d = ps.begin(P_STAR, false);
      d.count = 1;
      d.px = _muzzle.x;
      d.py = _muzzle.y;
      d.pz = _muzzle.z;
      d.life = 0.2;
      d.lifeVar = 0;
      d.size0 = 0.15 * k;
      d.size1 = bodyStar;
      d.sizeVar = 0;
      d.color0 = VFX.hot;
      d.color1 = VFX.hot;
      d.spin = 0.6;
      d.drag = 6;
      d.priority = 3;
      ps.emit();

      d = ps.begin(P_STAR, true);
      d.count = 1;
      d.px = _muzzle.x;
      d.py = _muzzle.y;
      d.pz = _muzzle.z;
      d.life = 0.16;
      d.lifeVar = 0;
      d.size0 = 0.15 * k;
      d.size1 = lightStar;
      d.sizeVar = 0;
      d.color0 = VFX.core;
      d.color1 = VFX.hot;
      d.intensity0 = 8;
      d.intensity1 = 2;
      d.bloom = 1;
      d.spin = 0.6;
      d.drag = 6;
      d.priority = 3;
      ps.emit();

      d = ps.begin(P_CHIP, false);
      d.count = 12;
      d.px = _muzzle.x;
      d.py = _muzzle.y;
      d.pz = _muzzle.z;
      d.posJitter = 0.06;
      d.vy = 0.5;
      d.velJitter = 5.5;
      d.gravity = -12;
      d.drag = 3.2;
      d.life = 0.36;
      d.lifeVar = 0.5;
      d.size0 = 0.09 * k;
      d.size1 = 0.03 * k;
      d.color0 = VFX.hot;
      d.color1 = VFX.danger;
      d.spinRate = 8;
      d.spinVar = 8;
      d.priority = 3;
      ps.emit();

      d = ps.begin(P_SPARK, true);
      d.count = 18;
      d.px = _muzzle.x;
      d.py = _muzzle.y;
      d.pz = _muzzle.z;
      d.posJitter = 0.06;
      d.vy = 0.5;
      d.velJitter = 5.5;
      d.gravity = -12;
      d.drag = 3.2;
      d.life = 0.32;
      d.lifeVar = 0.5;
      d.size0 = 0.15 * k;
      d.size1 = 0.01;
      d.color0 = VFX.core;
      d.color1 = VFX.hot;
      d.intensity0 = 5;
      d.intensity1 = 1.4;
      d.bloom = 1;
      d.stretch = 1.3;
      d.priority = 3;
      ps.emit();
    }

    if (isLocal) {
      this.burst(BURST_CATCH, chambered ? PALETTE.loaded : team);
      this.hooks.shake?.(chambered ? SHAKE_CATCH_CHAMBERED : SHAKE_CATCH_EMPTY);
    }
  }

  /** Deliberately unglamorous: a floor pickup gives you a paperweight. */
  private onPickup(playerId: string, w: WorldState): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const ps = this.particles;
    const d = ps.begin(P_PUFF, false);
    d.count = 4;
    d.px = p.pos.x;
    d.py = p.pos.y + 0.25;
    d.pz = p.pos.z;
    d.posJitter = 0.2;
    d.vy = 0.6;
    d.velJitter = 0.8;
    d.gravity = 0.4;
    d.drag = 3.4;
    d.life = 0.5;
    d.lifeVar = 0.3;
    d.size0 = 0.2;
    d.size1 = 0.75;
    d.color0 = VFX.spent;
    d.color1 = VFX.ink;
    d.spinRate = 1.2;
    d.spinVar = 2;
    d.priority = 1;
    ps.emit();
  }

  private onGunReset(gunId: number, w: WorldState): void {
    const gun = this.gunById(w, gunId);
    if (gun === null) return;
    const ps = this.particles;

    // An implosion: rings collapse inward, the opposite of every other effect here, so a
    // reset can never be mistaken for a throw or a catch.
    const d = ps.begin(P_RING, false);
    d.count = 3;
    d.px = gun.pos.x;
    d.py = gun.pos.y;
    d.pz = gun.pos.z;
    d.delayVar = 0.09;
    d.life = 0.3;
    d.lifeVar = 0.1;
    d.size0 = 2.6;
    d.size1 = 0.05;
    d.sizeVar = 0.15;
    d.color0 = teamColor(gun.team);
    d.color1 = VFX.ink;
    d.drag = 3;
    d.priority = 2;
    ps.emit();

    this.trails.gunLanded(gunId);
  }

  /** PULSE: a kinetic shell that genuinely bends what is behind it. */
  private onPulse(
    playerId: string,
    pos: Vec3,
    connected: boolean,
    w: WorldState,
    localId: string,
  ): void {
    const p = w.players[playerId];
    const team = p !== undefined ? teamColor(p.team) : VFX.ink;
    const isLocal = playerId === localId;

    // The sim's pulse origin is the caster's eye. For the local caster that is the
    // camera, and nothing anchored there survives the projection (see LOCAL_PULSE_PUSH)
    // — so the whole effect moves out in front of them, down the cone the shove travels.
    let ox = pos.x;
    let oy = pos.y;
    let oz = pos.z;
    if (isLocal && p !== undefined) {
      dirFromYawPitch(_d, p.yaw, p.pitch);
      ox += _d.x * LOCAL_PULSE_PUSH;
      oy += _d.y * LOCAL_PULSE_PUSH;
      oz += _d.z * LOCAL_PULSE_PUSH;
    }

    // A connected shove reads DANGER — someone was just thrown, or a pass was just
    // intercepted. A whiff is merely the thrower's own colour going nowhere.
    this.spawnShockwave(
      ox,
      oy,
      oz,
      connected ? PALETTE.danger : team,
      isLocal ? SHOCKWAVE_LOCAL_REACH : PULSE_RANGE * SHOCKWAVE_REACH,
    );

    const ps = this.particles;
    // The shock front is matte: PULSE has to be legible to the person being shoved, and
    // an additive ring across a white atrium is not legible to anyone.
    let d = ps.begin(P_RING, false);
    d.count = 2;
    d.px = ox;
    d.py = oy;
    d.pz = oz;
    d.delayVar = 0.06;
    d.life = 0.32;
    d.lifeVar = 0.1;
    d.size0 = 0.4;
    d.size1 = PULSE_RANGE * 2.1;
    d.sizeVar = 0.1;
    d.color0 = connected ? VFX.danger : team;
    d.color1 = team;
    d.drag = 3.6;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_DOT, false);
    d.count = 26;
    d.px = ox;
    d.py = oy;
    d.pz = oz;
    d.posJitter = 0.3;
    d.vy = 0.4;
    d.velJitter = 9;
    d.gravity = -6;
    d.drag = 4.5;
    d.life = 0.34;
    d.lifeVar = 0.4;
    d.size0 = 0.09;
    d.size1 = 0.02;
    d.color0 = team;
    d.color1 = VFX.ink;
    d.priority = 2;
    ps.emit();

    if (isLocal) {
      this.hooks.shake?.(connected ? SHAKE_PULSE_HIT : SHAKE_PULSE_WHIFF);
    }
  }

  /**
   * The highest-value unarmed play in the game. Celebrate it.
   *
   * Matte star and matte rings in the disarmer's colour, with an amber ember shower for
   * the round that just came loose. Every part of the silhouette is drawn rather than
   * added, because a disarm most often happens in the open — mid-pass, over a white
   * floor, in full sun — which is precisely where an additive celebration disappears.
   */
  private onDisarm(victimId: string, byId: string, w: WorldState, localId: string): void {
    const victim = w.players[victimId];
    const by = w.players[byId];
    const team = by !== undefined ? teamColor(by.team) : VFX.ink;
    const ps = this.particles;

    if (victim !== undefined) {
      const cx = victim.pos.x;
      const cy = victim.pos.y + PLAYER_HEIGHT * 0.62;
      const cz = victim.pos.z;
      // Stars are solid, so they are the third-person half of this effect only. Losing
      // the gun is the moment you most need to see the person who took it.
      const stars = victimId !== localId;

      let d = ps.begin(P_RING, false);
      d.count = 3;
      d.px = cx;
      d.py = cy;
      d.pz = cz;
      d.delayVar = 0.08;
      d.life = 0.4;
      d.lifeVar = 0.1;
      d.size0 = 0.3;
      d.size1 = 5.0;
      d.sizeVar = 0.2;
      d.color0 = team;
      d.color1 = team;
      d.drag = 3;
      d.priority = 3;
      ps.emit();

      if (stars) {
        // Taking the gun off somebody usually happens at PULSE range, i.e. under four
        // metres, which is exactly where an authored 3.6 m solid star stops being a
        // celebration and becomes a wipe over the person you just did it to.
        const bodyStar = this.capSolid(3.6, cx, cy, cz);
        const lightStar = this.capSolid(2.5, cx, cy, cz);

        d = ps.begin(P_STAR, false);
        d.count = 1;
        d.px = cx;
        d.py = cy;
        d.pz = cz;
        d.life = 0.22;
        d.lifeVar = 0;
        d.size0 = 0.4 * (bodyStar / 3.6);
        d.size1 = bodyStar;
        d.sizeVar = 0;
        d.color0 = team;
        d.color1 = team;
        d.spinRate = 4;
        d.drag = 6;
        d.priority = 3;
        ps.emit();

        d = ps.begin(P_STAR, true);
        d.count = 1;
        d.px = cx;
        d.py = cy;
        d.pz = cz;
        d.life = 0.2;
        d.lifeVar = 0;
        d.size0 = 0.3 * (lightStar / 2.5);
        d.size1 = lightStar;
        d.sizeVar = 0;
        d.color0 = VFX.core;
        d.color1 = team;
        d.intensity0 = 9;
        d.intensity1 = 1.8;
        d.bloom = 1;
        d.spinRate = 4;
        d.drag = 6;
        d.priority = 3;
        ps.emit();
      }

      // The loose round, thrown clear as matte embers plus the sparks that make them
      // burn. Amber here is exact: what was knocked out of their hands was a CHAMBER.
      d = ps.begin(P_CHIP, false);
      d.count = 18;
      d.px = cx;
      d.py = cy;
      d.pz = cz;
      d.posJitter = 0.16;
      d.vy = 1.2;
      d.velJitter = 12;
      d.gravity = -14;
      d.drag = 2.4;
      d.life = 0.5;
      d.lifeVar = 0.5;
      d.size0 = 0.13;
      d.size1 = 0.04;
      d.color0 = VFX.hot;
      d.color1 = VFX.ink;
      d.spinRate = 9;
      d.spinVar = 9;
      d.priority = 3;
      ps.emit();

      d = ps.begin(P_SPARK, true);
      d.count = 30;
      d.px = cx;
      d.py = cy;
      d.pz = cz;
      d.posJitter = 0.16;
      d.vy = 1.2;
      d.velJitter = 12;
      d.gravity = -14;
      d.drag = 2.4;
      d.life = 0.44;
      d.lifeVar = 0.5;
      d.size0 = 0.24;
      d.size1 = 0.01;
      d.color0 = VFX.core;
      d.color1 = VFX.hot;
      d.intensity0 = 6;
      d.intensity1 = 1.5;
      d.bloom = 1;
      d.stretch = 1.7;
      d.priority = 3;
      ps.emit();

      d = ps.begin(P_INK, false);
      d.count = 20;
      d.px = cx;
      d.py = cy;
      d.pz = cz;
      d.posJitter = 0.2;
      d.vy = 3.2;
      d.velJitter = 6;
      d.gravity = -20;
      d.drag = 1.1;
      d.life = 1.0;
      d.lifeVar = 0.5;
      d.size0 = 0.2;
      d.size1 = 0.06;
      d.color0 = team;
      d.color1 = VFX.ink;
      d.spinRate = 8;
      d.spinVar = 8;
      d.priority = 3;
      ps.emit();
    }

    if (byId === localId || victimId === localId) {
      this.burst(BURST_DISARM, team);
      this.hooks.shake?.(byId === localId ? SHAKE_DISARM_BY_ME : SHAKE_DISARMED);
    }
  }

  // --- movement -------------------------------------------------------------

  private onJump(playerId: string, double: boolean, w: WorldState, localId: string): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const ps = this.particles;

    if (double) {
      // The mid-air jump gets a hard graphic ring, because it is a resource being spent.
      const d = ps.begin(P_RING, false);
      d.count = 1;
      d.px = p.pos.x;
      d.py = p.pos.y + 0.2;
      d.pz = p.pos.z;
      d.life = 0.26;
      d.lifeVar = 0;
      d.size0 = 0.5;
      d.size1 = 2.6;
      d.sizeVar = 0;
      d.color0 = teamColor(p.team);
      d.color1 = VFX.dust;
      d.drag = 4;
      d.priority = 2;
      ps.emit();
    } else {
      this.impacts.dustPuff(p.pos.x, p.pos.y, p.pos.z, 0, 1, 0, 0.25, SurfaceKind.Concrete);
    }
    if (playerId === localId && double) this.hooks.shake?.(SHAKE_DOUBLE_JUMP);
  }

  private onLand(playerId: string, speed: number, surface: SurfaceKind, w: WorldState): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const s = clamp01(speed / 26);
    this.impacts.dustPuff(p.pos.x, p.pos.y, p.pos.z, 0, 1, 0, s, surface);
  }

  private onSlide(playerId: string, w: WorldState): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    this.impacts.dustPuff(p.pos.x, p.pos.y, p.pos.z, 0, 1, 0, 0.55, SurfaceKind.Concrete);
  }

  private onDash(playerId: string, dir: Vec3, w: WorldState, localId: string): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const isLocal = playerId === localId;
    this.trails.dash(p, isLocal);

    // Speed lines: a hard fan of stretched streaks thrown BACKWARD from the dash. Matte
    // chips, not sparks — a dash is movement, and movement gets ink, not light.
    //
    // The fan is thrown along -dir, which for the local player is *towards the camera*
    // whenever they dash backwards: twenty 18 cm chips arriving at the lens, on the one
    // input whose entire purpose is to get you somewhere while looking somewhere else.
    // So the local fan is smaller, shorter and emitted down at boot level, where it
    // reads as a kick off the floor and never crosses the crosshair.
    const ps = this.particles;
    const d = ps.begin(P_CHIP, false);
    d.count = isLocal ? 10 : 20;
    d.px = p.pos.x;
    d.py = p.pos.y + PLAYER_HEIGHT * (isLocal ? 0.14 : 0.5);
    d.pz = p.pos.z;
    d.posJitter = isLocal ? 0.24 : 0.4;
    d.vx = -dir.x * 12;
    d.vy = -dir.y * 12;
    d.vz = -dir.z * 12;
    d.spread = 0.55;
    d.speedVar = 0.5;
    d.velJitter = 2;
    d.gravity = isLocal ? -9 : -2;
    d.drag = 4.5;
    d.life = isLocal ? 0.2 : 0.28;
    d.lifeVar = 0.4;
    d.size0 = isLocal ? 0.08 : 0.18;
    d.size1 = isLocal ? 0.02 : 0.04;
    d.color0 = teamColor(p.team);
    d.color1 = VFX.ink;
    d.spinRate = 6;
    d.spinVar = 6;
    d.priority = 2;
    ps.emit();

    if (isLocal) this.hooks.shake?.(SHAKE_DASH);
  }

  private onWallrunStart(playerId: string, w: WorldState): void {
    const p = w.players[playerId];
    if (p === undefined || p.wallNormal === null) return;
    const n = p.wallNormal;
    this.impacts.dustPuff(
      p.pos.x + n.x * 0.4,
      p.pos.y + PLAYER_HEIGHT * 0.3,
      p.pos.z + n.z * 0.4,
      n.x,
      n.y,
      n.z,
      0.45,
      SurfaceKind.Concrete,
    );
  }

  private onWallJump(playerId: string, w: WorldState): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const n = p.wallNormal;
    const nx = n !== null ? n.x : 0;
    const ny = n !== null ? n.y : 1;
    const nz = n !== null ? n.z : 0;
    const ps = this.particles;

    const d = ps.begin(P_RING, false);
    d.count = 1;
    d.px = p.pos.x - nx * 0.35;
    d.py = p.pos.y + PLAYER_HEIGHT * 0.35;
    d.pz = p.pos.z - nz * 0.35;
    d.vx = nx * 2;
    d.vy = ny * 2;
    d.vz = nz * 2;
    d.life = 0.24;
    d.lifeVar = 0;
    d.size0 = 0.4;
    d.size1 = 2.4;
    d.sizeVar = 0;
    d.color0 = teamColor(p.team);
    d.color1 = VFX.dust;
    d.drag = 4;
    d.priority = 2;
    ps.emit();

    this.impacts.dustPuff(
      p.pos.x - nx * 0.35,
      p.pos.y + PLAYER_HEIGHT * 0.35,
      p.pos.z - nz * 0.35,
      nx,
      ny,
      nz,
      0.6,
      SurfaceKind.Concrete,
    );
  }

  /**
   * The hook leaving the hand.
   *
   * This used to be ten additive sparks, which over a sunlit white atrium is very nearly
   * nothing — so the one frame that confirms "the key did something" confirmed nothing at
   * all. It now leads with a MATTE collar and matte debris, the same bright-world rule
   * every other launch in this file follows, and keeps the sparks only as the bloom.
   */
  private onGrappleFire(playerId: string, w: WorldState, localId: string): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const isLocal = playerId === localId;
    handOrigin(p, isLocal, _muzzle);
    dirFromYawPitch(_d, p.yaw, p.pitch);
    const ps = this.particles;
    const k = isLocal ? LOCAL_HAND_SCALE : 1;

    // A hard ink collar punched forward along the shot. Ink, not team colour: firing a
    // grapple is movement, and movement carries no colour of its own in this game.
    let d = ps.begin(P_RING, false);
    d.count = 1;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.vx = _d.x * 6;
    d.vy = _d.y * 6;
    d.vz = _d.z * 6;
    d.life = 0.24;
    d.lifeVar = 0;
    d.size0 = 0.22 * k;
    d.size1 = 1.5 * k;
    d.sizeVar = 0;
    d.color0 = VFX.ink;
    d.color1 = VFX.dust;
    d.drag = 4;
    d.priority = 2;
    ps.emit();

    d = ps.begin(P_CHIP, false);
    d.count = 8;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.posJitter = 0.06 * k;
    d.vx = -_d.x * 3.5;
    d.vy = -_d.y * 3.5 + 0.6;
    d.vz = -_d.z * 3.5;
    d.spread = 0.7;
    d.speedVar = 0.6;
    d.velJitter = 1.6;
    d.gravity = -12;
    d.drag = 3.2;
    d.life = 0.26;
    d.lifeVar = 0.4;
    d.size0 = 0.09 * k;
    d.size1 = 0.02 * k;
    d.color0 = VFX.dust;
    d.color1 = VFX.ink;
    d.spinRate = 8;
    d.spinVar = 8;
    d.priority = 2;
    ps.emit();

    d = ps.begin(P_SPARK, true);
    d.count = 10;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.posJitter = 0.05;
    dirFromYawPitch(_d, p.yaw, p.pitch);
    d.vx = _d.x * 7;
    d.vy = _d.y * 7;
    d.vz = _d.z * 7;
    d.spread = 0.6;
    d.speedVar = 0.6;
    d.gravity = -6;
    d.drag = 5;
    d.life = 0.2;
    d.lifeVar = 0.4;
    d.size0 = 0.12;
    d.size1 = 0.01;
    d.color0 = VFX.core;
    d.color1 = teamColor(p.team);
    d.intensity0 = 3.4;
    d.intensity1 = 1.2;
    d.bloom = 1;
    d.stretch = 1.2;
    d.priority = 2;
    ps.emit();
  }

  /**
   * The bite. This is the frame that says the grapple WORKED, and it happens sixty metres
   * away from the player, so it has to be a drawn mark rather than a glow: a pair of matte
   * ink rings CONTRACTING onto the anchor, in the same language a catch uses on the hands.
   * The additive pair underneath supplies the bloom, and nothing more.
   */
  private onGrappleAttach(playerId: string, w: WorldState): void {
    const p = w.players[playerId];
    if (p === undefined || p.grapplePoint === null) return;
    const a = p.grapplePoint;
    const ps = this.particles;

    let d = ps.begin(P_RING, false);
    d.count = 2;
    d.px = a.x;
    d.py = a.y;
    d.pz = a.z;
    d.delayVar = 0.05;
    d.life = 0.26;
    d.lifeVar = 0.06;
    d.size0 = 2.4;
    d.size1 = 0.35;
    d.sizeVar = 0.1;
    d.color0 = VFX.ink;
    d.color1 = VFX.ink;
    d.drag = 2.6;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_CHIP, false);
    d.count = 10;
    d.px = a.x;
    d.py = a.y;
    d.pz = a.z;
    d.posJitter = 0.1;
    d.velJitter = 4.5;
    d.gravity = -14;
    d.drag = 2.6;
    d.life = 0.34;
    d.lifeVar = 0.5;
    d.size0 = 0.10;
    d.size1 = 0.03;
    d.color0 = VFX.dust;
    d.color1 = VFX.ink;
    d.spinRate = 9;
    d.spinVar = 9;
    d.priority = 2;
    ps.emit();

    d = ps.begin(P_RING, true);
    d.count = 1;
    d.px = a.x;
    d.py = a.y;
    d.pz = a.z;
    d.life = 0.3;
    d.lifeVar = 0;
    d.size0 = 0.15;
    d.size1 = 2.2;
    d.sizeVar = 0;
    d.color0 = VFX.core;
    d.color1 = teamColor(p.team);
    d.intensity0 = 4;
    d.intensity1 = 0.3;
    d.bloom = 1;
    d.drag = 4;
    d.priority = 2;
    ps.emit();

    d = ps.begin(P_SPARK, true);
    d.count = 14;
    d.px = a.x;
    d.py = a.y;
    d.pz = a.z;
    d.posJitter = 0.08;
    d.velJitter = 5;
    d.gravity = -14;
    d.drag = 2.6;
    d.life = 0.32;
    d.lifeVar = 0.5;
    d.size0 = 0.13;
    d.size1 = 0.01;
    d.color0 = VFX.core;
    d.color1 = teamColor(p.team);
    d.intensity0 = 4;
    d.intensity1 = 1.2;
    d.bloom = 1;
    d.stretch = 1.2;
    d.priority = 2;
    ps.emit();
  }

  /**
   * LETTING GO — and the payoff for letting go at the right moment.
   *
   * Two tiers, because the skill of the whole verb is the release timing
   * (GRAPPLE_RELEASE_BOOST) and a player can only learn a timing they can SEE:
   *
   *   every release   the tether snaps. A short matte ink flick off the hand, so the
   *                   tether never merely blinks out of existence and a mistimed release
   *                   is legibly a release rather than a bug.
   *   a boosted one   the full launch: matte chevrons thrown back down the flight path,
   *                   a hard ring, the bloom, and for the local player a screen burst
   *                   whose streaks sprint OUTWARD — the one graphic in the vocabulary
   *                   that means speed and nothing else.
   *
   * The gap between the two is the feedback loop. It used to be the gap between "a
   * firework" and "absolutely nothing", which teaches only that the verb is unreliable.
   */
  private onGrappleRelease(
    playerId: string,
    boosted: boolean,
    w: WorldState,
    localId: string,
  ): void {
    const p = w.players[playerId];
    if (p === undefined) return;
    const isLocal = playerId === localId;
    const ps = this.particles;
    const cy = p.pos.y + PLAYER_HEIGHT * 0.5;
    const speed = Math.hypot(p.vel.x, p.vel.y, p.vel.z) || 1;

    // --- the snap, on every release.
    handOrigin(p, isLocal, _muzzle);
    let d = ps.begin(P_CHIP, false);
    d.count = boosted ? 12 : 7;
    d.px = _muzzle.x;
    d.py = _muzzle.y;
    d.pz = _muzzle.z;
    d.posJitter = 0.08;
    d.velJitter = 4.5;
    d.gravity = -13;
    d.drag = 3.4;
    d.life = 0.26;
    d.lifeVar = 0.5;
    d.size0 = 0.08 * (isLocal ? LOCAL_HAND_SCALE : 1);
    d.size1 = 0.02;
    d.color0 = VFX.dust;
    d.color1 = VFX.ink;
    d.spinRate = 9;
    d.spinVar = 9;
    d.priority = 2;
    ps.emit();

    if (!boosted) return;

    // --- the launch. Matte first: this happens in mid-air over a white atrium, and the
    //     additive half below it has nothing to add to that.
    d = ps.begin(P_CHIP, false);
    d.count = 22;
    d.px = p.pos.x;
    d.py = cy;
    d.pz = p.pos.z;
    d.posJitter = 0.35;
    d.vx = (-p.vel.x / speed) * 13;
    d.vy = (-p.vel.y / speed) * 13;
    d.vz = (-p.vel.z / speed) * 13;
    d.spread = 0.5;
    d.speedVar = 0.5;
    d.velJitter = 2;
    d.gravity = -6;
    d.drag = 3.4;
    d.life = 0.34;
    d.lifeVar = 0.4;
    d.size0 = 0.17;
    d.size1 = 0.04;
    d.color0 = teamColor(p.team);
    d.color1 = VFX.ink;
    d.spinRate = 7;
    d.spinVar = 7;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_RING, false);
    d.count = 2;
    d.px = p.pos.x;
    d.py = cy;
    d.pz = p.pos.z;
    d.delayVar = 0.05;
    d.life = 0.3;
    d.lifeVar = 0.08;
    d.size0 = 0.4;
    d.size1 = 3.4;
    d.sizeVar = 0.12;
    d.color0 = VFX.ink;
    d.color1 = teamColor(p.team);
    d.drag = 3.4;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_RING, true);
    d.count = 2;
    d.px = p.pos.x;
    d.py = cy;
    d.pz = p.pos.z;
    d.delayVar = 0.06;
    d.life = 0.3;
    d.lifeVar = 0.1;
    d.size0 = 0.4;
    d.size1 = 3.0;
    d.sizeVar = 0.1;
    d.color0 = VFX.core;
    d.color1 = teamColor(p.team);
    d.intensity0 = 4;
    d.intensity1 = 0.3;
    d.bloom = 1;
    d.drag = 3.4;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_SPARK, true);
    d.count = 18;
    d.px = p.pos.x;
    d.py = cy;
    d.pz = p.pos.z;
    d.posJitter = 0.3;
    d.vx = (-p.vel.x / speed) * 10;
    d.vy = (-p.vel.y / speed) * 10;
    d.vz = (-p.vel.z / speed) * 10;
    d.spread = 0.6;
    d.speedVar = 0.5;
    d.gravity = -4;
    d.drag = 4;
    d.life = 0.3;
    d.lifeVar = 0.4;
    d.size0 = 0.22;
    d.size1 = 0.02;
    d.color0 = VFX.core;
    d.color1 = teamColor(p.team);
    d.intensity0 = 3.6;
    d.intensity1 = 1;
    d.bloom = 1;
    d.stretch = 1.8;
    d.priority = 2;
    ps.emit();

    if (isLocal) {
      this.burst(BURST_LAUNCH, teamColor(p.team));
      this.hooks.shake?.(SHAKE_GRAPPLE_BOOST);
    }
  }

  // --- match flow -----------------------------------------------------------

  private onRoundStart(): void {
    // A round begins on a clean arena: no stale ink, no stale trails, no stale debris.
    this.particles.clear();
    this.trails.reset();
    this.impacts.reset();
    this.woundMarkerT.clear();
    this.noonAmount = 0;
    this.woundPulse = 0;
    this.wasWounded = false;
    // INK, not white. The round-start wipe is two printed bars crossing the frame; in
    // white they would be a wipe across a white world, which is to say nothing at all.
    this.burst(BURST_ROUND_START, VFX.ink);
  }

  private onRoundEnd(winner: number, w: WorldState, localId: string): void {
    const me = w.players[localId];
    // Won: your colour slams the border. Lost or drawn: ink, flat and joyless.
    let color: number = VFX.ink;
    if (winner >= 0) color = teamColor(winner);
    if (me !== undefined && winner >= 0) {
      color = winner === me.team ? teamColor(winner) : VFX.ink;
    }
    this.burst(BURST_ROUND_END, color);
    this.noonAmount = 0;
    this.hooks.shake?.(SHAKE_ROUND_END);
  }

  private onSuddenDeath(): void {
    this.noonAmount = 1;
    this.burst(BURST_ROUND_END, PALETTE.loaded);
    this.hooks.shake?.(SHAKE_SUDDEN_DEATH);
  }

  private onMatchEnd(winner: number, w: WorldState, localId: string): void {
    const me = w.players[localId];
    const win = me !== undefined && winner === me.team;
    this.burst(BURST_ROUND_END, win ? teamColor(winner) : VFX.ink);
    this.hooks.inkSplash?.(winner >= 0 ? teamColor(winner) : VFX.ink);
    this.hooks.shake?.(SHAKE_MATCH_END);
  }

  // -------------------------------------------------------------------------
  // Per-frame
  // -------------------------------------------------------------------------

  update(dtSec: number, w: WorldState, localId: string): void {
    if (this.disposed) return;
    const dt = dtSec > 0.25 ? 0.25 : dtSec > 0 ? dtSec : 0;
    this.time += dt;
    this.frame++;

    this.syncViewer(w, localId);
    this.particles.update(dt);
    this.trails.update(dt, w, localId);
    this.impacts.update(dt);

    this.syncEnvironment();
    this.updateOverlays(dt, w, localId);
    this.updateShockwaves(dt);
    this.updateBeacons(w);
    this.updateWoundMarkers(dt, w);
    this.updateAnchorMarks(dt, w, localId);
  }

  /**
   * Mark every hookable anchor, and mark HARD the one the key would actually take.
   *
   * This is the whole answer to "I pressed the grapple key and nothing happened": the
   * anchors were always there and always in range, and the game never once said so.
   */
  private updateAnchorMarks(dt: number, w: WorldState, localId: string): void {
    const p = w.players[localId];
    if (p === undefined || p.healthState === HealthState.Dead || this.anchors.length === 0) {
      this.reticle.hide();
      return;
    }

    const ex = p.pos.x;
    const ey = eyeY(p);
    const ez = p.pos.z;
    dirFromYawPitch(_aim, p.yaw, p.pitch);

    // Already attached to a PLACED anchor: that anchor IS the target, whatever the crosshair
    // is doing now. A free-surface grapple (GRAPPLE_SURFACE) has no anchor to highlight — the
    // tether itself shows where it went — so fall through to the crosshair prediction.
    const bestId =
      p.grappleAnchorId >= 0
        ? p.grappleAnchorId
        : pickAnchorForView(this.anchors, ex, ey, ez, _aim.x, _aim.y, _aim.z);

    this.reticle.update(
      dt,
      this.anchors,
      ex,
      ey,
      ez,
      _aim.x,
      _aim.y,
      _aim.z,
      bestId,
      p.grappleCooldownMs > 0 ? 0 : 1,
      this.time,
      this.camera !== null ? this.camera.fov : FOV_BASE,
    );
  }

  /**
   * Track the local player's eye and hand it to the impact system.
   *
   * This is the one piece of "where is the camera" this file needs, and it deliberately
   * comes from the SIM rather than from the Three.js camera: the camera carries shake,
   * roll and an interpolation delay, none of which should be able to change how big an
   * effect is authored, and it may not even exist yet on the first frame.
   */
  private syncViewer(w: WorldState, localId: string): void {
    const me = w.players[localId];
    if (me === undefined) return;
    this.viewer.x = me.pos.x;
    this.viewer.y = eyeY(me);
    this.viewer.z = me.pos.z;
    this.hasViewer = true;
    this.impacts.setViewer(this.viewer.x, this.viewer.y, this.viewer.z);
  }

  /**
   * Clamp a SOLID shape's size so it cannot swallow the frame at close range.
   * See {@link SOLID_MAX_ANGULAR}. Returns the size unchanged when it is already safe,
   * which is the overwhelmingly common case.
   */
  private capSolid(size: number, x: number, y: number, z: number): number {
    if (!this.hasViewer) return size;
    const dx = x - this.viewer.x;
    const dy = y - this.viewer.y;
    const dz = z - this.viewer.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const cap = (dist > SOLID_MIN_DIST ? dist : SOLID_MIN_DIST) * SOLID_MAX_ANGULAR;
    return size < cap ? size : cap;
  }

  private syncEnvironment(): void {
    this.particles.syncFog(this.scene.fog);
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    if (dpr !== this.dpr) {
      this.dpr = dpr;
      this.particles.setPixelRatio(dpr);
    }
    if (this.camera === null) this.findCamera();
  }

  private findCamera(): void {
    const children = this.scene.children;
    for (let i = 0; i < children.length; i++) {
      const c = children[i] as THREE.PerspectiveCamera;
      if ((c as unknown as { isPerspectiveCamera?: boolean }).isPerspectiveCamera === true) {
        this.camera = c;
        // Screen-space treatments live on the camera so they inherit shake and roll and
        // therefore stay locked to the frame no matter what the camera is doing.
        c.add(this.wound.mesh);
        c.add(this.noon.mesh);
        for (const b of this.bursts) c.add(b.overlay.mesh);
        return;
      }
    }
  }

  private updateOverlays(dt: number, w: WorldState, localId: string): void {
    const cam = this.camera;

    const me = w.players[localId];
    const wounded = me !== undefined && me.healthState === HealthState.Wounded;
    const woundTarget = wounded ? 1 : 0;
    // The rim itself ramps in fast and out slow: the state change must be felt instantly,
    // and the relief of a heal should be savoured. It is only ever the thin persistent
    // rim — the loud part of being hit is the pulse below, which is over in 0.25s.
    const rate = woundTarget > this.woundAmount ? 9 : 3.2;
    this.woundAmount += (woundTarget - this.woundAmount) * (1 - Math.exp(-rate * dt));
    if (this.woundAmount < 0.002) this.woundAmount = 0;

    // Fired by the `wound` event, and also by the state edge so a snapshot that arrives
    // without its event (a reconnect, a mid-round join) still punctuates.
    if (wounded && !this.wasWounded) this.woundPulse = 1;
    this.wasWounded = wounded;
    if (this.woundPulse > 0) {
      this.woundPulse -= dt / WOUND_PULSE_SEC;
      if (this.woundPulse < 0) this.woundPulse = 0;
    }
    // Squared, so the hit lands hard and leaves quickly rather than sitting at half
    // strength for an eighth of a second.
    const pulse = this.woundPulse * this.woundPulse;

    const noonTarget = w.match.suddenDeath ? 1 : 0;
    this.noonAmount += (noonTarget - this.noonAmount) * (1 - Math.exp(-2.6 * dt));
    if (this.noonAmount < 0.002) this.noonAmount = 0;

    this.wound.material.uniforms.uSteady.value = this.woundAmount;
    this.wound.material.uniforms.uPulse.value = pulse;
    this.wound.material.uniforms.uTime.value = this.time;
    this.wound.mesh.visible = this.woundAmount > 0.004 || pulse > 0.004;

    this.noon.material.uniforms.uAmount.value = this.noonAmount;
    this.noon.material.uniforms.uTime.value = this.time;
    this.noon.mesh.visible = this.noonAmount > 0.004;

    for (const b of this.bursts) {
      if (!b.live) continue;
      b.age += dt;
      const t = b.age / b.duration;
      if (t >= 1) {
        b.live = false;
        b.overlay.mesh.visible = false;
        b.overlay.material.uniforms.uKind.value = 0;
        continue;
      }
      b.overlay.material.uniforms.uProgress.value = t;
    }

    if (cam === null) return;
    if (this.wound.mesh.visible) this.wound.fit(cam);
    if (this.noon.mesh.visible) this.noon.fit(cam);
    for (const b of this.bursts) {
      if (b.live) b.overlay.fit(cam);
    }
  }

  private burst(kind: number, color: number): void {
    // Recycle the oldest slot if all three are busy, so a chaotic moment never drops the
    // most recent (and therefore most relevant) piece of feedback.
    let slot: BurstSlot | null = null;
    let oldest: BurstSlot | null = null;
    for (const b of this.bursts) {
      if (!b.live) {
        slot = b;
        break;
      }
      if (oldest === null || b.age > oldest.age) oldest = b;
    }
    if (slot === null) slot = oldest;
    if (slot === null) return;

    slot.live = true;
    slot.age = 0;
    slot.duration = BURST_DURATION[kind] ?? 0.4;
    const u = slot.overlay.material.uniforms;
    u.uKind.value = kind;
    u.uProgress.value = 0;
    u.uOpacity.value = BURST_OPACITY[kind] ?? 0.5;
    u.uSeed.value = this.particles.random();
    (u.uColor.value as THREE.Color).setHex(color, THREE.SRGBColorSpace);
    slot.overlay.mesh.visible = true;
    if (this.camera !== null) slot.overlay.fit(this.camera);
  }

  // --- shockwaves -----------------------------------------------------------

  private spawnShockwave(
    x: number,
    y: number,
    z: number,
    color: number,
    reach: number,
  ): void {
    let slot: ShockwaveSlot | null = null;
    let oldest: ShockwaveSlot | null = null;
    for (const s of this.shocks) {
      if (!s.live) {
        slot = s;
        break;
      }
      if (oldest === null || s.age > oldest.age) oldest = s;
    }
    if (slot === null) slot = oldest;
    if (slot === null) return;

    slot.live = true;
    slot.age = 0;
    slot.radius = 0.4;
    slot.reach = reach;
    slot.mesh.position.set(x, y, z);
    slot.mesh.scale.setScalar(slot.radius);
    slot.mesh.visible = true;
    (slot.material.uniforms.uColor.value as THREE.Color).setHex(color, THREE.SRGBColorSpace);
    slot.material.uniforms.uProgress.value = 0;
  }

  private updateShockwaves(dt: number): void {
    let active = 0;
    for (const s of this.shocks) {
      if (!s.live) continue;
      s.age += dt;
      const t = s.age / SHOCKWAVE_LIFE;
      if (t >= 1) {
        s.live = false;
        s.mesh.visible = false;
        s.material.uniforms.uHasScene.value = 0;
        continue;
      }
      // Expands fast and then coasts — a shove, not a balloon.
      const e = 1 - Math.pow(1 - t, 2.4);
      s.radius = 0.4 + e * s.reach;
      s.mesh.scale.setScalar(s.radius);
      s.material.uniforms.uProgress.value = t;
      active++;
    }

    // Release the grab texture once nothing has needed it for a while. A full-resolution
    // half-float copy is not something to keep resident for a whole match.
    if (active === 0) {
      this.idleGrabTime += dt;
      if (this.idleGrabTime > 6 && this.sceneTexture !== null) {
        this.sceneTexture.dispose();
        this.sceneTexture = null;
        this.grabW = 0;
        this.grabH = 0;
        for (const s of this.shocks) {
          s.material.uniforms.uScene.value = null;
          s.material.uniforms.uHasScene.value = 0;
        }
      }
    } else {
      this.idleGrabTime = 0;
    }
  }

  /**
   * Copy the frame rendered so far into a texture so the shell can refract it.
   *
   * Runs at most once per frame, from the first shockwave that draws. If the driver
   * refuses the copy — different backing format, an exotic context — we detect it once,
   * disable grabbing for the session and fall back to the non-refractive shell, which
   * still reads as a shockwave. The GL error state is only inspected on the very first
   * grab, because querying it forces a pipeline stall.
   */
  private grabScene(renderer: THREE.WebGLRenderer, mat: THREE.ShaderMaterial): void {
    if (!this.grabSupported) {
      mat.uniforms.uHasScene.value = 0;
      return;
    }

    if (this.grabFrame !== this.frame) {
      this.grabFrame = this.frame;
      renderer.getDrawingBufferSize(_size);
      const w = Math.max(1, Math.floor(_size.x));
      const h = Math.max(1, Math.floor(_size.y));

      if (this.sceneTexture === null || this.grabW !== w || this.grabH !== h) {
        if (this.sceneTexture !== null) this.sceneTexture.dispose();
        // Must match the composer's HDR target exactly: copyTexSubImage2D refuses to
        // convert between a half-float colour buffer and an 8-bit texture.
        const tex = new THREE.FramebufferTexture(w, h);
        tex.name = 'vfx-scene-grab';
        tex.type = THREE.HalfFloatType;
        tex.format = THREE.RGBAFormat;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.generateMipmaps = false;
        this.sceneTexture = tex;
        this.grabW = w;
        this.grabH = h;
      }

      const gl = renderer.getContext();
      if (!this.grabChecked) {
        // Drain any pre-existing error so the check below is about our copy alone.
        let guard = 0;
        while (gl.getError() !== gl.NO_ERROR && guard++ < 8) {
          /* drain */
        }
      }

      renderer.copyFramebufferToTexture(this.sceneTexture);

      if (!this.grabChecked) {
        this.grabChecked = true;
        if (gl.getError() !== gl.NO_ERROR) {
          this.grabSupported = false;
          this.sceneTexture.dispose();
          this.sceneTexture = null;
          this.grabW = 0;
          this.grabH = 0;
        }
      }
    }

    if (this.sceneTexture === null) {
      mat.uniforms.uHasScene.value = 0;
      return;
    }
    mat.uniforms.uScene.value = this.sceneTexture;
    (mat.uniforms.uResolution.value as THREE.Vector2).set(this.grabW, this.grabH);
    mat.uniforms.uHasScene.value = 1;
  }

  // --- beacons --------------------------------------------------------------

  private updateBeacons(w: WorldState): void {
    const on = w.match.suddenDeath;
    let used = 0;
    if (on) {
      for (let i = 0; i < w.guns.length && used < this.beacons.length; i++) {
        const g = w.guns[i];
        if (g.state === GunState.Void) continue;
        const b = this.beacons[used++];
        b.group.visible = true;
        b.group.position.set(g.pos.x, g.pos.y - 0.4, g.pos.z);
        (b.material.uniforms.uColor.value as THREE.Color).setHex(
          g.chambered ? PALETTE.loaded : teamColor(g.team),
          THREE.SRGBColorSpace,
        );
        b.material.uniforms.uTime.value = this.time;
        b.material.uniforms.uAmount.value = this.noonAmount * (g.chambered ? 1 : 0.65);
      }
    }
    for (let i = used; i < this.beacons.length; i++) this.beacons[i].group.visible = false;
  }

  // --- wound markers --------------------------------------------------------

  /**
   * A small pulsing ink drop over every wounded player. Depth-tested on purpose: this is
   * intel you earn by having line of sight, not a wallhack.
   *
   * Matte, because this marker's entire job is to be spotted across a bright room. It is
   * the enemy-facing half of the wound state (DESIGN.md 1) and it cannot be allowed to
   * depend on the wall behind it being dark.
   */
  private updateWoundMarkers(dt: number, w: WorldState): void {
    // Drop bookkeeping for anyone who left while wounded. The loop below only visits ids
    // that are still in the snapshot, so without this a disconnect mid-wound leaves an
    // entry in the map for the rest of the session.
    //
    // Swept about once a second rather than every frame: `keys()` allocates an iterator,
    // this system does not allocate during a match, and a stale entry costs nothing at
    // all for the extra second it survives.
    if (this.woundMarkerT.size > 0 && this.frame % WOUND_MARKER_SWEEP_FRAMES === 0) {
      for (const id of this.woundMarkerT.keys()) {
        if (w.players[id] === undefined) this.woundMarkerT.delete(id);
      }
    }

    for (const id in w.players) {
      const p = w.players[id];
      if (p.healthState !== HealthState.Wounded) {
        if (this.woundMarkerT.has(id)) this.woundMarkerT.delete(id);
        continue;
      }
      const t = (this.woundMarkerT.get(id) ?? WOUND_MARKER_PERIOD) + dt;
      if (t < WOUND_MARKER_PERIOD) {
        this.woundMarkerT.set(id, t);
        continue;
      }
      this.woundMarkerT.set(id, 0);

      const ps = this.particles;
      const y = p.pos.y + PLAYER_HEIGHT + 0.42;
      let d = ps.begin(P_INK, false);
      d.count = 1;
      d.px = p.pos.x;
      d.py = y;
      d.pz = p.pos.z;
      d.vy = 0.35;
      d.gravity = 0;
      d.drag = 2.2;
      d.life = WOUND_MARKER_PERIOD * 0.95;
      d.lifeVar = 0;
      d.size0 = 0.16;
      d.size1 = 0.30;
      d.sizeVar = 0;
      d.color0 = VFX.danger;
      d.color1 = VFX.dangerDeep;
      d.priority = 2;
      ps.emit();

      d = ps.begin(P_RING, false);
      d.count = 1;
      d.px = p.pos.x;
      d.py = y;
      d.pz = p.pos.z;
      d.vy = 0.35;
      d.gravity = 0;
      d.drag = 2.2;
      d.life = 0.36;
      d.lifeVar = 0;
      d.size0 = 0.18;
      d.size1 = 0.72;
      d.sizeVar = 0;
      d.color0 = VFX.danger;
      d.color1 = VFX.dangerDeep;
      d.priority = 1;
      ps.emit();

      // Ink hits the floor beneath a grounded wounded player, marking their route.
      if (p.grounded) {
        this.impacts.splat(
          p.pos.x + (ps.random() - 0.5) * 0.35,
          p.pos.y + 0.02,
          p.pos.z + (ps.random() - 0.5) * 0.35,
          0,
          1,
          0,
          0.22 + ps.random() * 0.18,
          VFX.dangerDeep,
          VFX.danger,
          9,
        );
      }
    }
  }

  // -------------------------------------------------------------------------

  private gunById(w: WorldState, id: number): GunEntity | null {
    for (let i = 0; i < w.guns.length; i++) {
      if (w.guns[i].id === id) return w.guns[i];
    }
    return null;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.trails.dispose();
    this.impacts.dispose();
    this.particles.dispose();

    if (this.camera !== null) {
      this.camera.remove(this.wound.mesh);
      this.camera.remove(this.noon.mesh);
      for (const b of this.bursts) this.camera.remove(b.overlay.mesh);
    }
    this.wound.dispose();
    this.noon.dispose();
    for (const b of this.bursts) b.overlay.dispose();
    this.overlayGeometry.dispose();

    for (const s of this.shocks) {
      this.scene.remove(s.mesh);
      s.mesh.onBeforeRender = (): void => {};
      s.material.dispose();
    }
    this.shocks.length = 0;
    this.shockGeometry.dispose();

    for (const b of this.beacons) {
      this.scene.remove(b.group);
      b.group.clear();
      b.material.dispose();
    }
    this.beacons.length = 0;
    this.beaconGeometry.dispose();

    this.scene.remove(this.reticle.mesh);
    this.reticle.dispose();

    if (this.sceneTexture !== null) {
      this.sceneTexture.dispose();
      this.sceneTexture = null;
    }

    this.woundMarkerT.clear();
    this.camera = null;
  }
}
